/* dp-theme.js — section interactions, populated incrementally */

/* ============================================================
   Accessibility helper for drawers and overlays (WCAG 2.4.3, 4.1.2).
   While a panel is closed it is `inert`, so keyboard and screen
   reader users can't land on invisible controls. Opening moves focus
   into the panel and makes the rest of the page inert, so Tab can't
   escape into content hidden behind it (2.4.3); closing undoes that
   and returns focus to whatever opened it. Siblings marked
   [data-dp-panel-backdrop] stay clickable so click-to-close works.
   ============================================================ */
window.DP = window.DP || {};
window.DP.panelA11y = function (el) {
  if (!el) return { open: function () {}, close: function () {} };
  var opener = null;
  var walled = [];
  var FOCUSABLE = 'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  el.inert = !el.classList.contains('is-open');

  // Inert every sibling of el and of each of its ancestors up to <body>,
  // remembering which ones we touched so close() only restores those.
  function wallOff() {
    for (var node = el; node && node !== document.body; node = node.parentElement) {
      var parent = node.parentElement;
      if (!parent) break;
      Array.prototype.forEach.call(parent.children, function (sib) {
        if (sib === node || sib.inert || sib.hasAttribute('data-dp-panel-backdrop') || /^(SCRIPT|STYLE|LINK)$/.test(sib.tagName)) return;
        sib.inert = true;
        walled.push(sib);
      });
    }
  }
  function unwall() {
    walled.forEach(function (sib) { sib.inert = false; });
    walled = [];
  }

  return {
    open: function (focusFirst) {
      if (!el.inert) return; // already open
      opener = document.activeElement;
      el.inert = false;
      wallOff();
      if (focusFirst !== false) {
        var first = el.querySelector(FOCUSABLE);
        if (first) requestAnimationFrame(function () { first.focus({ preventScroll: true }); });
      }
    },
    close: function () {
      if (el.inert) return; // already closed
      el.inert = true;
      unwall();
      if (opener && document.contains(opener) && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
      opener = null;
    }
  };
};

/* ============================================================
   Shared qty-input helper
   Wires focus / Enter / blur on a container so that editable
   qty inputs commit only on blur (or Enter→blur), restore on
   invalid input, and skip unchanged values.
   onCommit(input, newQty) is called with the validated qty.
   ============================================================ */
window.DP = window.DP || {};
window.DP.bindQtyInputs = function (container, selector, onCommit) {
  container.addEventListener('focus', function (e) {
    const input = e.target.closest(selector);
    if (input) input.dataset.qtyOriginal = input.value;
  }, true);

  container.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    const input = e.target.closest(selector);
    if (!input) return;
    e.preventDefault();
    input.blur();
  });

  container.addEventListener('blur', function (e) {
    const input = e.target.closest(selector);
    if (!input) return;
    const raw = input.value.trim();
    const original = parseInt(input.dataset.qtyOriginal, 10);
    if (raw === '' || isNaN(parseInt(raw, 10))) { input.value = original; return; }
    const qty = Math.max(0, parseInt(raw, 10));
    if (qty === original) return;
    onCommit(input, qty);
  }, true);
};

/* ============================================================
   dp-announcement module — rotate center messages, update donut
   ============================================================ */
(function () {
  window.DP.register('announcement', function () {
    const root = document.querySelector('[data-dp-announcement]');
    if (!root) return;

    // Rotate messages
    const messages = root.querySelectorAll('.dp-announcement__message');
    if (messages.length > 1) {
      const interval = parseInt(root.dataset.rotateMs, 10) || 4500;
      let i = 0;
      setInterval(() => {
        messages[i].classList.remove('is-active');
        i = (i + 1) % messages.length;
        messages[i].classList.add('is-active');
      }, interval);
    }

    // Update donut from live cart
    const fill = root.querySelector('.dp-announcement__donut-fill');
    const label = root.querySelector('[data-dp-progress-label]');
    const threshold = parseInt(root.dataset.threshold, 10) || 0;
    if (!fill || !label || !threshold) return;

    function render(cartTotalCents) {
      const pct = Math.min(100, Math.round((cartTotalCents / threshold) * 100));
      fill.setAttribute('stroke-dasharray', `${pct} ${100 - pct}`);
      if (cartTotalCents >= threshold) {
        label.textContent = label.dataset.unlocked || 'Free shipping unlocked';
      } else {
        const remaining = window.DP.formatMoney(threshold - cartTotalCents);
        label.textContent = `${remaining} to free shipping`;
      }
    }

    window.DP.cart.get().then((cart) => render(cart.total_price));

    document.addEventListener('dp:cart:updated', (e) => render(e.detail.total_price));
  });
})();

/* ============================================================
   dp-header module
   ============================================================ */
(function () {
  window.DP.register('header', function () {
    const header = document.querySelector('[data-dp-header]');
    if (!header) return;

    // Sticky on scroll — must apply to the section wrapper, not the inner
    // element, because the inner element's containing block is only as
    // tall as the header itself, so position:sticky on it releases immediately.
    const sticky = header.dataset.sticky === 'true';
    const section = header.closest('.dp-header-section');
    if (sticky && section) {
      section.style.position = 'sticky';
      section.style.top = '0';
      section.style.zIndex = '1000';
      let lastY = 0;
      window.addEventListener('scroll', window.DP.throttle(() => {
        const y = window.scrollY;
        header.classList.toggle('is-scrolled', y > 60);
        section.classList.toggle('is-hidden', y > lastY && y > 200);
        lastY = y;
      }, 100), { passive: true });
    }

    // Mobile menu — slide-in sidebar. Toggle the drawer, its backdrop,
    // and body scroll-lock together. Clicking the backdrop also fires
    // this action (the backdrop element carries data-dp-action).
    const mobileNav = document.querySelector('[data-dp-mobile-nav]');
    const mobileBackdrop = document.querySelector('[data-dp-mobile-nav-backdrop]');
    const mobileNavA11y = window.DP.panelA11y(mobileNav);
    window.DP.bindAction('toggle-mobile-menu', () => {
      const willOpen = !mobileNav?.classList.contains('is-open');
      mobileNav?.classList.toggle('is-open', willOpen);
      mobileBackdrop?.classList.toggle('is-open', willOpen);
      document.body.classList.toggle('dp-nav-open', willOpen);
      document.body.style.overflow = willOpen ? 'hidden' : '';
      willOpen ? mobileNavA11y.open() : mobileNavA11y.close();
    });
    // ESC closes the drawer.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileNav?.classList.contains('is-open')) {
        mobileNav.classList.remove('is-open');
        mobileBackdrop?.classList.remove('is-open');
        document.body.classList.remove('dp-nav-open');
        document.body.style.overflow = '';
        mobileNavA11y.close();
      }
    });

    // Mobile submenu expanders. Each item with children has a chevron
    // button that toggles its sibling .dp-header__mobile-sub panel. We
    // walk up from the clicked button to the row, then grab the next
    // sibling (the panel) — keeps the markup flat and easy to reason
    // about.
    window.DP.bindAction('toggle-mobile-submenu', (btn) => {
      const row = btn.closest('.dp-header__mobile-nav-row');
      const panel = row && row.nextElementSibling;
      if (!panel || !panel.classList.contains('dp-header__mobile-sub')) return;
      const isOpen = !panel.hasAttribute('hidden');
      if (isOpen) {
        panel.setAttribute('hidden', '');
        btn.setAttribute('aria-expanded', 'false');
      } else {
        panel.removeAttribute('hidden');
        btn.setAttribute('aria-expanded', 'true');
      }
    });

    // Nav hover underline is CSS-only (::after on each link), no JS needed.

    // FIX (customer report, Aug 2026): the mega-menu dropdown panel (see
    // sections/dp-header.liquid, `.dp-nav__panel`) opens purely via CSS
    // `:hover` / `:focus-within` — there is no JS-driven open/close for
    // it. That works fine with a mouse, but on touch devices (reported
    // specifically on iPad) there is no real `:hover` state, so the
    // panel never opens and users can't see the products nested under
    // that menu item — their tap just navigates to the top-level link's
    // href instead.
    //
    // This only touches devices that lack real hover (coarse pointer /
    // touch), detected via the same `(hover: none), (pointer: coarse)`
    // media query already used elsewhere in this file. Desktop mouse
    // users are completely unaffected — their existing CSS-hover
    // behavior is left as-is.
    //
    // Behavior: first tap on a top-level item WITH children opens the
    // panel (and does NOT navigate). A second tap on the same link (or
    // any tap once the panel is open) navigates normally, since intent
    // to view the submenu has already been satisfied. Tapping anywhere
    // outside the open panel closes it.
    (function initTouchMegaMenu() {
      const isCoarsePointer = window.matchMedia('(hover: none), (pointer: coarse)').matches;
      if (!isCoarsePointer) return;

      const itemsWithChildren = document.querySelectorAll('.dp-nav__item--has-children');
      if (itemsWithChildren.length === 0) return;

      function closePanel(item) {
        const toggle = item.querySelector(':scope > [data-dp-nav-toggle]');
        const panel = item.querySelector(':scope > .dp-nav__panel');
        item.classList.remove('is-touch-open');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        if (panel) {
          panel.style.removeProperty('opacity');
          panel.style.removeProperty('visibility');
          panel.style.removeProperty('pointer-events');
          panel.style.removeProperty('display');
        }
      }

      function openPanel(item) {
        const toggle = item.querySelector(':scope > [data-dp-nav-toggle]');
        const panel = item.querySelector(':scope > .dp-nav__panel');
        item.classList.add('is-touch-open');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
        if (panel) {
          // setProperty(..., 'important') is the native JS way to force
          // !important on an inline style — more reliable than injecting
          // a separate <style> block (which was tried first and did not
          // work reliably).
          panel.style.setProperty('opacity', '1', 'important');
          panel.style.setProperty('visibility', 'visible', 'important');
          panel.style.setProperty('pointer-events', 'auto', 'important');
          panel.style.setProperty('display', 'block', 'important');
        }
      }

      itemsWithChildren.forEach((item) => {
        const link = item.querySelector(':scope > .dp-nav__link');
        if (!link) return;
        link.addEventListener('click', function (e) {
          const isOpen = item.classList.contains('is-touch-open');
          if (isOpen) return; // second tap navigates normally
          // preventDefault + stopPropagation as the very first thing,
          // and bound in the CAPTURE phase (true, last arg below) so
          // this runs BEFORE any other click handler that might also
          // be attached to this link and could trigger navigation
          // first (this fixes the "first tap navigates immediately"
          // bug — our handler previously ran in the bubble phase and
          // lost the race).
          e.preventDefault();
          e.stopPropagation();
          itemsWithChildren.forEach((other) => { if (other !== item) closePanel(other); });
          openPanel(item);
        }, true);
      });

      // Tapping outside any open panel closes it.
      document.addEventListener('click', (e) => {
        itemsWithChildren.forEach((item) => {
          if (item.classList.contains('is-touch-open') && !item.contains(e.target)) {
            closePanel(item);
          }
        });
      });
    })();

    // a11y: keyboard / screen reader access to the mega-menu (WCAG 2.1.1,
    // 4.1.2). The panel no longer opens on :focus-within, so Tab moves
    // link → caret → next link without walking every sublink. The caret
    // button opens the panel; Esc closes it and returns focus to the
    // caret; tabbing out of the item or clicking elsewhere closes it.
    (function initKeyboardMegaMenu() {
      const items = document.querySelectorAll('.dp-nav__item--has-children');
      if (items.length === 0) return;

      function setOpen(item, open) {
        const toggle = item.querySelector(':scope > [data-dp-nav-toggle]');
        item.classList.toggle('is-open', open);
        if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      items.forEach((item) => {
        const toggle = item.querySelector(':scope > [data-dp-nav-toggle]');
        if (!toggle) return;

        toggle.addEventListener('click', () => {
          const willOpen = !item.classList.contains('is-open');
          items.forEach((other) => { if (other !== item) setOpen(other, false); });
          setOpen(item, willOpen);
        });

        item.addEventListener('keydown', (e) => {
          if (e.key !== 'Escape' || !item.classList.contains('is-open')) return;
          setOpen(item, false);
          toggle.focus();
        });

        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) setOpen(item, false);
        });
      });

      document.addEventListener('click', (e) => {
        items.forEach((item) => {
          if (item.classList.contains('is-open') && !item.contains(e.target)) setOpen(item, false);
        });
      });
    })();

    // Cart count badge
    function updateCartCount(count) {
      const badge = header.querySelector('[data-dp-cart-count]');
      if (badge) badge.textContent = count > 0 ? count : '';
    }
    window.DP.cart.get().then(cart => updateCartCount(cart.item_count));
    document.addEventListener('dp:cart:updated', e => updateCartCount(e.detail.item_count));
  });
})();

/* ============================================================
   dp-mini-cart module
   ============================================================ */
(function () {
  window.DP.register('mini-cart', function () {
    const mc = document.querySelector('[data-dp-mini-cart]');
    if (!mc) return;

    const mcA11y = window.DP.panelA11y(mc);
    // Backdrop carries data-dp-action="close-mini-cart", so clicking
    // outside the drawer closes it.
    const backdrop = document.querySelector('[data-dp-mini-cart-backdrop]');
    function open() {
      if (window.location.pathname === '/cart') return;
      mc.classList.add('is-open');
      backdrop?.classList.add('is-open');
      mcA11y.open();
      window.DP.cart.get().then(render);
    }
    function close() { if (!mc.classList.contains('is-open')) return; mc.classList.remove('is-open'); backdrop?.classList.remove('is-open'); mcA11y.close(); }

    // Group bundle children under their parent. The Divine Power
    // product-options-app tags each cart line with underscore-prefixed
    // properties: `_bundle_id` (shared across parent+children),
    // `_is_bundle_parent: "true"` on the parent, `_bundle_child: "true"`
    // on children. The parent also carries the human-readable selection
    // labels (e.g. "Switches", "Keycaps") as ADDITIONAL non-underscored
    // properties. We render only parents; children become indented
    // sub-bullets under the parent's title, and the parent's displayed
    // price is the bundle total.
    function groupBundles(items) {
      const bundles = new Map(); // bundle_id → { parent, children, total }
      const loose = [];
      items.forEach(item => {
        const props = item.properties || {};
        const bundleId = props._bundle_id;
        if (!bundleId) { loose.push(item); return; }
        if (!bundles.has(bundleId)) bundles.set(bundleId, { parent: null, children: [], total: 0 });
        const b = bundles.get(bundleId);
        b.total += item.final_line_price || 0;
        if (props._is_bundle_parent === 'true' || props._is_bundle_parent === true) b.parent = item;
        else b.children.push(item);
      });
      // Preserve original item order; emit parent once, skip children.
      const out = [];
      const seenBundles = new Set();
      items.forEach(item => {
        const props = item.properties || {};
        const bundleId = props._bundle_id;
        if (!bundleId) { out.push({ kind: 'loose', item }); return; }
        if (seenBundles.has(bundleId)) return;
        seenBundles.add(bundleId);
        const b = bundles.get(bundleId);
        if (b.parent) out.push({ kind: 'bundle', parent: b.parent, children: b.children, total: b.total });
      });
      return out;
    }

    function bundleLabels(parent) {
      // Build the bullet list under a bundle parent's title.
      // Two sources merged into one visual list (variant options first,
      // then configurator properties):
      //   1) Native variant options (`options_with_values`) — meaningful
      //      since the theme variant picker block was added to
      //      configurator products like Iris CE / Iris LM / Quefrency LM.
      //      Without this branch the drawer skipped the user's Case
      //      selection entirely. Skip the "Default Title" sentinel
      //      Shopify assigns to single-variant products.
      //   2) Plain-keyed line-item properties (Switches, Keycaps, Cable,
      //      etc.) — emitted by the DP options-app configurator into the
      //      cart-add bundle expansion.
      const out = [];
      const opts = parent.options_with_values;
      if (Array.isArray(opts)) {
        opts.forEach(o => {
          if (!o || !o.value || o.value === 'Default Title') return;
          out.push({ key: o.name, value: o.value });
        });
      }
      const props = parent.properties || {};
      Object.keys(props).forEach(k => {
        if (!k || k.charAt(0) === '_') return;
        const v = props[k];
        if (v == null || v === '') return;
        out.push({ key: k, value: v });
      });
      return out;
    }

    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, ch => ({
        '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
      }[ch]));
    }

    function controlsHtml(qty) {
      // D4: qty +/− and remove controls in the drawer. Hooked via the
      // global `data-dp-action` delegate; handlers below call /cart/change
      // (or /cart/update for bundles) and rely on `dp:cart:updated` event
      // to re-render the drawer in place.
      return `
        <div class="dp-mini-cart__item-controls">
          <div class="dp-mini-cart__qty" role="group" aria-label="Quantity">
            <button type="button"
                    class="dp-mini-cart__qty-btn"
                    data-dp-action="mini-cart-decrement"
                    aria-label="Decrease quantity">−</button>
            <input type="number" class="dp-mini-cart__qty-value" value="${qty}" min="0" aria-live="polite" data-dp-mini-cart-qty-input>
            <button type="button"
                    class="dp-mini-cart__qty-btn"
                    data-dp-action="mini-cart-increment"
                    aria-label="Increase quantity">+</button>
          </div>
          <button type="button"
                  class="dp-mini-cart__remove"
                  data-dp-action="mini-cart-remove"
                  aria-label="Remove from cart">×</button>
        </div>`;
    }

    const TAG_ICON = '<svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';
    // Per-line discount markup: struck original price + one line per
    // line-level allocation (cart-level discounts aren't allocated to
    // lines in /cart.js, so those only show in the footer).
    function lineDiscountHtml(lines) {
      // Bundles: each line has its own allocation of the same code, so
      // merge by title to get one row per code.
      const totals = new Map();
      lines.forEach(l => (l.line_level_discount_allocations || []).forEach(a => {
        const title = a.discount_application && a.discount_application.title;
        if (title && a.amount > 0) totals.set(title, (totals.get(title) || 0) + a.amount);
      }));
      if (!totals.size) return '';
      return `<ul class="dp-mini-cart__item-discounts">${Array.from(totals, ([title, amount]) =>
        `<li>${TAG_ICON} ${esc(title)} (\u2212${window.DP.formatMoney(amount)})</li>`
      ).join('')}</ul>`;
    }
    function priceHtml(lines, finalCents) {
      const original = lines.reduce((sum, l) => sum + (l.original_line_price || 0), 0);
      const struck = original > finalCents
        ? `<s class="dp-cart-subtotal__original">${window.DP.formatMoney(original)}</s>`
        : '';
      return `<p class="dp-mini-cart__item-price">${struck}${window.DP.formatMoney(finalCents)}</p>`;
    }

    function renderRow(entry) {
      if (entry.kind === 'loose') {
        const item = entry.item;
        // D-011: title + image link to the product page. `item.url`
        // comes from /cart.js and points at the variant's storefront
        // URL with ?variant=… preserved, so clicking takes the user
        // back to the exact configuration they added.
        const url = esc(item.url || '#');
        // GWP free gift → locked line: show "Free", no qty/remove controls.
        const isGift = !!(item.properties && item.properties._gwp);
        return `
          <div class="dp-mini-cart__item${isGift ? ' dp-mini-cart__item--gift' : ''}"
               data-dp-line-key="${esc(item.key)}"
               data-dp-qty="${item.quantity || 1}">
            <a class="dp-mini-cart__item-image-link" href="${url}" aria-label="${esc(item.product_title)}">
              <img src="${esc(item.featured_image?.url || item.image || '')}" alt="${esc(item.product_title)}" loading="lazy">
            </a>
            <div class="dp-mini-cart__item-info">
              <p class="dp-mini-cart__item-title"><a href="${url}" class="dp-mini-cart__item-title-link">${esc(item.product_title)}</a></p>
              ${isGift
                ? `<p class="dp-mini-cart__item-gift-tag">${esc((window.DP && window.DP.gwpLabel) || 'Free')}</p>`
                : priceHtml([item], item.final_line_price)}
              ${isGift ? '' : (item.variant_title ? `<p class="dp-mini-cart__item-variant">${esc(item.variant_title)}</p>` : '')}
              ${isGift ? '' : lineDiscountHtml([item])}
              ${isGift ? '' : controlsHtml(item.quantity || 1)}
            </div>
          </div>`;
      }
      // bundle
      const parent = entry.parent;
      const labels = bundleLabels(parent);
      const labelsHtml = labels.length
        ? `<ul class="dp-mini-cart__bundle-components">${labels.map(l =>
            `<li><strong>${esc(l.key)}:</strong> ${esc(l.value)}</li>`
          ).join('')}</ul>`
        : '';
      // For bundles we operate on ALL line keys atomically — the
      // cart-transformer Function isn't activated yet (escalated to Juan),
      // so qty changes / removals here must hit each line individually.
      // Bundle qty is the parent's quantity (children are sync'd at add-
      // time by the DP options app).
      const bundleKeys = [parent.key].concat(entry.children.map(c => c.key)).join(',');
      const bundleQty = parent.quantity || 1;
      const parentUrl = esc(parent.url || '#');
      return `
        <div class="dp-mini-cart__item dp-mini-cart__item--bundle"
             data-dp-line-key="${esc(parent.key)}"
             data-dp-bundle-keys="${esc(bundleKeys)}"
             data-dp-qty="${bundleQty}">
          <a class="dp-mini-cart__item-image-link" href="${parentUrl}" aria-label="${esc(parent.product_title)}">
            <img src="${esc(parent.featured_image?.url || parent.image || '')}" alt="${esc(parent.product_title)}" loading="lazy">
          </a>
          <div class="dp-mini-cart__item-info">
            <p class="dp-mini-cart__item-title"><a href="${parentUrl}" class="dp-mini-cart__item-title-link">${esc(parent.product_title)}</a></p>
            ${priceHtml([parent].concat(entry.children), entry.total)}
            ${labelsHtml}
            ${lineDiscountHtml([parent].concat(entry.children))}
            ${controlsHtml(bundleQty)}
          </div>
        </div>`;
    }

    function renderShippingBar(cart) {
      // D4 follow-on: drawer free-shipping reminder, country-aware.
      // - US qualifying (cart >= threshold): full bar + ✓ message
      // - US below threshold: progress bar + "Add $X for free shipping"
      // - Intl: bar hidden — Estimate Shipping link below covers them
      // - Empty cart: bar hidden
      const wrap = mc.querySelector('.dp-mini-cart__shipping-bar');
      if (!wrap) return;
      const fill = wrap.querySelector('[data-dp-mini-cart-shipping-fill]');
      const label = wrap.querySelector('[data-dp-mini-cart-shipping-label]');
      const country = window.DP.country || 'US';
      const isUS = country === 'US';
      const threshold = window.DP.freeShippingThresholdCents || 15000;
      const total = cart.total_price || 0;

      if (!cart.item_count || !isUS) {
        wrap.style.display = 'none';
        wrap.removeAttribute('data-dp-shipping-state');
        return;
      }

      wrap.style.display = '';
      if (total >= threshold) {
        wrap.dataset.dpShippingState = 'qualifies';
        if (fill) fill.style.width = '100%';
        if (label) label.textContent = '🚚 Free Standard shipping in the US ✓';
      } else {
        wrap.dataset.dpShippingState = 'progress';
        const pct = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));
        const remaining = (threshold - total) / 100;
        const remainingStr = remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        if (fill) fill.style.width = pct + '%';
        if (label) label.textContent = "You're $" + remainingStr + ' away from free US shipping';
      }
    }

    function render(cart) {
      const items = mc.querySelector('[data-dp-mini-cart-items]');
      const subtotal = mc.querySelector('[data-dp-mini-cart-subtotal]');
      if (!items) return;
      if (!cart.item_count) {
        items.innerHTML = '';
        mc.classList.add('is-empty');
      } else {
        mc.classList.remove('is-empty');
        const rows = groupBundles(cart.items || []);
        items.innerHTML = rows.map(renderRow).join('');
      }
      if (subtotal) subtotal.textContent = window.DP.formatMoney(cart.total_price);
      renderDiscounts(cart);
      renderShippingBar(cart);
    }

    // Sum cart-level and product-level discounts per title (a code's
    // title is the code itself) so both kinds show in the drawer.
    function renderDiscounts(cart) {
      const list = mc.querySelector('[data-dp-mini-cart-discounts]');
      const original = mc.querySelector('[data-dp-mini-cart-original]');
      const totals = new Map();
      (cart.cart_level_discount_applications || []).forEach(app => {
        totals.set(app.title, (totals.get(app.title) || 0) + app.total_allocated_amount);
      });
      (cart.items || []).forEach(item => {
        (item.line_level_discount_allocations || []).forEach(alloc => {
          const title = alloc.discount_application && alloc.discount_application.title;
          if (title) totals.set(title, (totals.get(title) || 0) + alloc.amount);
        });
      });
      if (list) {
        list.innerHTML = Array.from(totals, ([title, amount]) =>
          `<li><span>${TAG_ICON} ${esc(title)}</span><span>−${window.DP.formatMoney(amount)}</span></li>`
        ).join('');
        list.hidden = totals.size === 0;
      }
      if (original) {
        const discounted = cart.original_total_price > cart.total_price;
        original.textContent = discounted ? window.DP.formatMoney(cart.original_total_price) : '';
        original.hidden = !discounted;
      }
    }

    window.DP.bindAction('toggle-mini-cart', () => mc.classList.contains('is-open') ? close() : open());
    window.DP.bindAction('close-mini-cart', close);

    // D4: drawer-scoped qty / remove handlers. Use mini-cart-* action
    // names (rather than reusing qty-increment / qty-decrement / remove-
    // line which the cart page registers) because the cart-page handlers
    // hard-reload on success — that closes the drawer mid-interaction.
    // These handlers rely on `dp:cart:updated` (dispatched by DP.cart.*)
    // to re-render the drawer in place.
    function lineContext(el) {
      const item = el.closest('.dp-mini-cart__item');
      if (!item) return null;
      const bundleKeys = item.dataset.dpBundleKeys;
      const qty = parseInt(item.dataset.dpQty, 10) || 1;
      if (bundleKeys) {
        return { kind: 'bundle', keys: bundleKeys.split(','), qty: qty };
      }
      const lineKey = item.dataset.dpLineKey;
      if (!lineKey) return null;
      return { kind: 'loose', key: lineKey, qty: qty };
    }

    function setAllLoading(busy) {
      mc.querySelectorAll('.dp-mini-cart__item').forEach(item => item.classList.toggle('is-loading', busy));
    }

    function applyQty(ctx, newQty) {
      setAllLoading(true);
      let p;
      if (ctx.kind === 'bundle') {
        const updates = {};
        ctx.keys.forEach(k => { updates[k] = newQty; });
        p = window.DP.cart.update({ updates: updates });
      } else {
        p = window.DP.cart.change(ctx.key, newQty);
      }
      return p.catch(function (err) {
        setAllLoading(false);
        return Promise.reject(err);
      });
    }

    window.DP.bindAction('mini-cart-decrement', function (el) {
      const ctx = lineContext(el);
      if (!ctx) return;
      applyQty(ctx, Math.max(0, ctx.qty - 1)).catch(function (err) {
        console.warn('[mini-cart] decrement failed', err);
      });
    });

    window.DP.bindAction('mini-cart-increment', function (el) {
      const ctx = lineContext(el);
      if (!ctx) return;
      applyQty(ctx, ctx.qty + 1).catch(function (err) {
        console.warn('[mini-cart] increment failed', err);
      });
    });

    window.DP.bindAction('mini-cart-remove', function (el) {
      const ctx = lineContext(el);
      if (!ctx) return;
      applyQty(ctx, 0).catch(function (err) {
        console.warn('[mini-cart] remove failed', err);
      });
    });

    window.DP.bindQtyInputs(mc, '[data-dp-mini-cart-qty-input]', function (input, qty) {
      const ctx = lineContext(input);
      if (!ctx) return;
      applyQty(ctx, qty).catch(function (err) {
        console.warn('[mini-cart] qty input failed', err);
      });
    });

    document.addEventListener('dp:cart:updated', e => render(e.detail));
  });
})();

/* ============================================================
   dp-search-overlay module
   ============================================================ */
(function () {
  window.DP.register('search-overlay', function () {
    const overlay = document.querySelector('[data-dp-search-overlay]');
    if (!overlay) return;
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, ch => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[ch]));
    const input = overlay.querySelector('[data-dp-search-input]');
    const results = overlay.querySelector('[data-dp-search-results]');
    const maxResults = parseInt(overlay.dataset.maxResults || '8', 10);

    const overlayA11y = window.DP.panelA11y(overlay);
    function open() { overlay.classList.add('is-open'); overlayA11y.open(false); input?.focus(); }
    function close() { overlay.classList.remove('is-open'); overlayA11y.close(); if (input) input.value = ''; if (results) results.innerHTML = ''; }

    function search(q) {
      if (!q || q.length < 2) { if (results) results.innerHTML = ''; return; }
      fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=${maxResults}`)
        .then(r => r.json())
        .then(data => {
          const products = data.resources?.results?.products || [];
          if (!results) return;
          if (!products.length) { results.innerHTML = '<p class="dp-search-overlay__no-results">No results found.</p>'; return; }
          results.innerHTML = products.map(p => `
            <a class="dp-search-overlay__result" href="${esc(p.url)}">
              <img src="${esc(p.featured_image?.url || '')}" alt="${esc(p.title)}" width="48" height="48" loading="lazy">
              <span class="dp-search-overlay__result-title">${esc(p.title)}</span>
              <span class="dp-search-overlay__result-price">${window.DP.formatMoney(p.price)}</span>
            </a>
          `).join('');
        })
        .catch(() => { if (results) results.innerHTML = ''; });
    }

    window.DP.bindAction('open-search', open);
    window.DP.bindAction('close-search', close);
    input?.addEventListener('input', window.DP.debounce(e => search(e.target.value), 250));
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && overlay.classList.contains('is-open')) close(); });
  });
})();

/* ============================================================
   dp-cart-drawer module
   ============================================================ */
(function () {
  window.DP.register('cart-drawer', function () {
    const drawer = document.querySelector('[data-dp-drawer]');
    if (!drawer) return;
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, ch => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[ch]));
    const root = drawer.closest('.dp-cart-drawer');
    const rootA11y = window.DP.panelA11y(root);

    function open() {
      root.classList.add('is-open');
      rootA11y.open();
      document.body.style.overflow = 'hidden';
      window.DP.cart.get().then(render);
    }
    function close() {
      if (!root.classList.contains('is-open')) return;
      root.classList.remove('is-open');
      rootA11y.close();
      document.body.style.overflow = '';
    }
    function render(cart) {
      const body = drawer.querySelector('[data-dp-drawer-body]');
      if (!body) return;
      if (cart.item_count === 0) {
        body.innerHTML = '<p class="dp-cart-drawer__empty">Your cart is empty.</p>';
        return;
      }
      body.innerHTML = cart.items.map((item) => `
        <div class="dp-cart-drawer__item" data-dp-line-key="${esc(item.key)}">
          <img src="${esc(item.image)}" alt="${esc(item.product_title)}" width="64" height="64"/>
          <div class="dp-cart-drawer__item-info">
            <div class="dp-cart-drawer__item-title">${esc(item.product_title)}</div>
            <div class="dp-cart-drawer__item-price">${window.DP.formatMoney(item.line_price)}</div>
            <div class="dp-cart-drawer__item-qty">Qty: ${esc(item.quantity)}</div>
          </div>
        </div>
      `).join('');
      const subtotal = drawer.querySelector('[data-dp-drawer-subtotal]');
      if (subtotal) subtotal.textContent = window.DP.formatMoney(cart.total_price);
    }

    window.DP.bindAction('open-cart-drawer',  open);
    window.DP.bindAction('close-cart-drawer', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    document.addEventListener('dp:cart:updated', (e) => render(e.detail));
  });
})();

/* ============================================================
   dp-product-scroller
   ============================================================ */
(function () {
  window.DP.register('product-scroller', function () {
    const roots = document.querySelectorAll('.dp-product-scroller-section, [data-dp-scroller]');
    roots.forEach((root) => {
      const track = root.querySelector('[data-dp-scroll-track]');
      if (!track) return;

      function stepDelta(dir) {
        const first = track.querySelector('.dp-cross-sells__item, .dp-pcard, [data-dp-scroll-item]');
        const cardWidth = first ? first.getBoundingClientRect().width : track.clientWidth * 0.8;
        const gap = parseFloat(getComputedStyle(track).gap) || 0;
        return dir * (cardWidth + gap);
      }
      function updateState() {
        const max = track.scrollWidth - track.clientWidth;
        root.querySelectorAll('[data-dp-scroll-btn]').forEach((btn) => {
          const dir = parseInt(btn.dataset.dpScrollBtn, 10);
          if (dir < 0) btn.disabled = track.scrollLeft <= 2;
          else btn.disabled = track.scrollLeft >= max - 2;
        });
      }

      root.querySelectorAll('[data-dp-scroll-btn]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const raw = parseInt(btn.dataset.dpScrollBtn, 10);
          // If the attr is ±1 treat as step-by-card; larger numbers keep legacy px-delta behavior.
          const delta = Math.abs(raw) <= 2 ? stepDelta(raw) : raw;
          track.scrollBy({ left: delta, behavior: 'smooth' });
        });
      });
      track.addEventListener('scroll', window.DP.debounce ? window.DP.debounce(updateState, 60) : updateState, { passive: true });
      updateState();
    });
  });
})();

/* ============================================================
   dp-collection-hero — category card scroll arrows (mobile)
   ============================================================ */
(function () {
  window.DP.register('collection-hero-scroll', function () {
    document.querySelectorAll('.collection-types').forEach((root) => {
      const track = root.querySelector('.collection-types__scroll');
      const prev  = root.querySelector('.types-prev');
      const next  = root.querySelector('.types-next');
      if (!track || !prev || !next) return;

      function updateState() {
        const maxScroll = track.scrollWidth - track.clientWidth;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= maxScroll - 2;
      }
      function step(dir) {
        const card = track.querySelector('.type-card');
        const delta = card ? (card.offsetWidth + 16) * dir : 240 * dir;
        track.scrollBy({ left: delta, behavior: 'smooth' });
      }

      prev.addEventListener('click', () => step(-1));
      next.addEventListener('click', () => step(1));
      track.addEventListener('scroll', window.DP.debounce(updateState, 50), { passive: true });
      updateState();
    });
  });
})();

/* ============================================================
   dp-price-badges
   ------------------------------------------------------------
   Single source of truth for the compare-at / save-badge markup
   next to a price, shared by this file's initial-page-load pass
   AND the native variant-picker's per-change updates below
   (dp-product-variants → updatePriceWrap).

   Configurator products (Divine Power options-app, detected via
   its server-rendered `[data-product-options]` mount) let the
   customer add upsells on top of the base variant. The variant's
   `compare_at_price` never accounted for those upsells, so once
   any are added:
     - the % savings shrinks even though the discount itself hasn't
       changed (e.g. $50 off $100 is 50%, but $50 off a $130
       configured total reads as ~38% — wrong number, same deal)
     - the crossed-out price (variant compare_at_price) no longer
       represents "what this configuration would cost without the
       discount" — it's missing the upsell cost entirely
   For configurator products we keep the fixed dollar savings
   (compare_at − price, base variant only — that number stays true
   regardless of which upsells get added) but derive the crossed-out
   price as `configured price + that fixed savings`, and keep it
   pinned to the LIVE configured price via a MutationObserver, since
   the options-app rewrites the price leaf's text on every upsell
   change with no event we can hook into directly. Non-configurator
   products are unaffected and keep the existing static compare-at
   price + % badge.
   ============================================================ */
(function () {
  const DP = window.DP = window.DP || {};

  function fmt(cents) {
    return DP.formatMoney ? DP.formatMoney(cents) : '$' + (cents / 100).toFixed(2);
  }

  function renderCompare(wrap, compareCents) {
    let compareEl = wrap.querySelector('.dp-price__compare');
    if (!compareEl) {
      compareEl = document.createElement('span');
      compareEl.className = 'dp-price__compare';
      compareEl.innerHTML = '<s></s>';
      wrap.appendChild(compareEl);
    }
    compareEl.style.display = '';
    const s = compareEl.querySelector('s') || compareEl;
    s.textContent = fmt(compareCents);
  }

  function renderSave(wrap, text) {
    let saveEl = wrap.querySelector('.dp-price__save');
    if (!saveEl) {
      saveEl = document.createElement('span');
      saveEl.className = 'dp-price__save';
      wrap.appendChild(saveEl);
    }
    saveEl.style.display = '';
    saveEl.textContent = text;
  }

  /* Keeps the crossed-out price in lockstep with the options-app's
     live rewrites of the price leaf. One observer per wrap, set up
     lazily the first time that wrap needs configurator handling. */
  function ensureConfiguratorWatcher(wrap) {
    if (wrap.dpConfiguratorObserver) return;
    const priceEl = wrap.querySelector('[data-product-price]');
    if (!priceEl) return;
    const observer = new MutationObserver(() => {
      const saveCents = parseInt(wrap.dataset.dpSaveCents, 10);
      if (isNaN(saveCents)) return;
      const currentCents = DP.parsePriceTextToCents && DP.parsePriceTextToCents(priceEl.textContent);
      if (currentCents == null) return;
      renderCompare(wrap, currentCents + saveCents);
    });
    observer.observe(priceEl, { childList: true, characterData: true, subtree: true });
    wrap.dpConfiguratorObserver = observer;
  }

  /* `priceCents`/`compareCents` are the BASE VARIANT's numbers (no
     upsells) — called right when a variant is selected (or on page
     init), the one moment both are guaranteed in sync. */
  DP.applyPriceBadges = function (wrap, priceCents, compareCents) {
    if (!wrap) return;
    if (!(compareCents && compareCents > priceCents)) {
      const compareEl = wrap.querySelector('.dp-price__compare');
      const saveEl = wrap.querySelector('.dp-price__save');
      if (compareEl) compareEl.style.display = 'none';
      if (saveEl) saveEl.style.display = 'none';
      delete wrap.dataset.dpSaveCents;
      return;
    }

    const saveCents = compareCents - priceCents;
    const isConfigurator = !!document.querySelector('[data-product-options]');

    if (isConfigurator) {
      wrap.dataset.dpSaveCents = saveCents;
      renderCompare(wrap, priceCents + saveCents); // == compareCents right now
      renderSave(wrap, 'Sale - ' + fmt(saveCents) + ' Off');
      ensureConfiguratorWatcher(wrap);
      return;
    }

    delete wrap.dataset.dpSaveCents;
    renderCompare(wrap, compareCents);
    const off = Math.round((saveCents / compareCents) * 100);
    renderSave(wrap, 'Save ' + off + '%');
  };

  function init() {
    document.querySelectorAll('.dp-price[data-compare-cents]').forEach((wrap) => {
      const priceCents = parseInt(wrap.dataset.priceCents, 10);
      const compareCents = parseInt(wrap.dataset.compareCents, 10);
      if (isNaN(priceCents) || isNaN(compareCents)) return;
      DP.applyPriceBadges(wrap, priceCents, compareCents);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================
   dp-product-variants — native variant picker for the dp-product
   section. Handles radio buttons AND <select> dropdowns. Reads the
   variants JSON from <script data-dp-variants-json="<section.id>">
   embedded by sections/dp-product.liquid → `variants` block.

   Responsibilities on selection change:
     - read all selected option[N] values
     - match against variants by option1/option2/option3
     - update hidden `id` on every form inside the section
       (both [data-dp-atc-form] and [data-dp-express-form])
     - update [data-product-price] (rendered by snippets/dp-price.liquid)
     - update the legend value label (data-dp-option-value)
     - dispatch `variant:change` on document so other modules
       (image swap, sticky-ATC, Klaviyo viewed-product) react
     - replace ?variant= in the URL via history.replaceState
     - mark ATC button disabled when variant is unavailable
   ============================================================ */
(function () {
  window.DP.register('product-variants', function () {
    document.querySelectorAll('[data-dp-variant-picker]').forEach((picker) => {
      if (picker.__dpVariantBound) return;
      picker.__dpVariantBound = true;

      const section = picker.closest('.dp-product-section, .dp-product-section-simple') || picker.closest('section') || document;
      // The variants JSON is keyed by section.id. We don't know the section.id
      // from JS reliably, but each picker's nearest sibling <script
      // data-dp-variants-json> belongs to it. Look up by walking the section.
      const jsonScript = section.querySelector('[data-dp-variants-json]');
      if (!jsonScript) return;
      let variants;
      try {
        variants = JSON.parse(jsonScript.textContent.trim());
      } catch (err) {
        console.error('[dp] variant JSON parse failed:', err);
        return;
      }
      if (!Array.isArray(variants) || variants.length === 0) return;

      const optionInputs = picker.querySelectorAll('[data-dp-variant-option]');
      const fieldsets = picker.querySelectorAll('.dp-product__option');

      function readSelected() {
        const selected = [];
        fieldsets.forEach((fs) => {
          const pos = parseInt(fs.dataset.optionPosition, 10) - 1;
          const sel = fs.querySelector('select[data-dp-variant-option]');
          if (sel) {
            selected[pos] = sel.value;
            return;
          }
          const radio = fs.querySelector('input[type="radio"][data-dp-variant-option]:checked');
          if (radio) selected[pos] = radio.value;
        });
        return selected;
      }

      function matchVariant(selected) {
        return variants.find((v) => {
          // Shopify variant.options is an array of strings; alt accessors
          // are option1/option2/option3.
          if (Array.isArray(v.options)) {
            return v.options.every((val, i) => val === selected[i]);
          }
          return (
            (v.option1 || null) === (selected[0] || null) &&
            (v.option2 || null) === (selected[1] || null) &&
            (v.option3 || null) === (selected[2] || null)
          );
        });
      }

      function updateLegendValues() {
        fieldsets.forEach((fs) => {
          const valueEl = fs.querySelector('[data-dp-option-value]');
          if (!valueEl) return;
          const sel = fs.querySelector('select[data-dp-variant-option]');
          if (sel) { valueEl.textContent = sel.value; return; }
          const radio = fs.querySelector('input[type="radio"][data-dp-variant-option]:checked');
          if (radio) valueEl.textContent = radio.value;
        });
      }

      function updateRadioSelectedClass() {
        picker.querySelectorAll('.dp-product__radio, .dp-product__swatch').forEach((label) => {
          const input = label.querySelector('input[type="radio"]');
          label.classList.toggle('is-selected', !!(input && input.checked));
        });
      }

      /* G4 (D-007): variant dependency cascade.
         For each option group, for each option value, ask: "if the
         user picked THIS value while keeping their other current
         selections, is there an available variant?" Mark
         unavailable values with `is-sold-out` (text pills) or
         `is-unavailable` (color swatches) so the picker reflects
         what's actually buyable for the current combination.

         Without this, the picker rendered availability based on
         each option-value's FIRST matching variant (Liquid-time
         render), which is wrong as soon as the user changes any
         other option. Cite: MBK Keycaps — Black+2u Convex has 22
         in stock, White+2u Convex has 95, all other 12 colors are
         0, but "2u Convex" displayed available regardless of color
         because Black (the first variant) had stock. */
      function updateOptionAvailability() {
        const selected = readSelected();
        fieldsets.forEach((fs) => {
          const pos = parseInt(fs.dataset.optionPosition, 10) - 1;
          if (isNaN(pos)) return;
          const labels = fs.querySelectorAll('.dp-product__radio, .dp-product__swatch');
          labels.forEach((label) => {
            const input = label.querySelector('input[type="radio"][data-dp-variant-option]');
            if (!input) return;
            const value = input.value;

            // Hypothetical selection: current selection but with THIS
            // value swapped in at this position.
            const hypothetical = selected.slice();
            hypothetical[pos] = value;

            // Find any variant matching the hypothetical AND available.
            const found = variants.find((v) => {
              const opts = Array.isArray(v.options)
                ? v.options
                : [v.option1, v.option2, v.option3];
              for (let i = 0; i < hypothetical.length; i++) {
                if (!hypothetical[i]) continue;
                if ((opts[i] || null) !== hypothetical[i]) return false;
              }
              return v.available;
            });

            // Toggle both class names; CSS for swatches uses
            // is-unavailable, CSS for text pills uses is-sold-out.
            // Setting both is safe — only the matching one applies.
            label.classList.toggle('is-sold-out', !found);
            label.classList.toggle('is-unavailable', !found);
            if (found) input.removeAttribute('data-sold-out');
            else input.setAttribute('data-sold-out', 'true');
          });
        });
      }

      function updatePrice(variant) {
        if (!variant) return;
        /* Scope: every `[data-product-price]` on the page EXCEPT
           line-total displays (sticky ATC). The merchant reported
           the sticky ATC wasn't reflecting variant changes for
           non-configurator products (e.g. cherry-icebergo-pbt-keyset)
           because the old `section.querySelector('[data-product-price]')`
           only walked the PDP section. Sticky ATC is its own page-level
           section, so its price element was missed entirely.
           Broadening to `document.querySelectorAll(...)` updates ALL
           unit-price displays everywhere on the page. Line totals
           (sticky ATC) are handled separately by updateLineTotals
           below — they read this unit price + multiply by qty. */
        const priceWraps = document.querySelectorAll('[data-product-price]:not([data-dp-line-total] [data-product-price]):not([data-dp-line-total])');
        priceWraps.forEach((priceWrap) => updatePriceWrap(priceWrap, variant));
      }
      function updatePriceWrap(priceEl, variant) {
        /* `priceEl` is the number-only leaf (`[data-product-price]`,
           see snippets/dp-price.liquid) — compare/save badges are its
           FLEX SIBLINGS under `.dp-price`, not its children. Keeping
           them out of priceEl is what stops the options-app's blanket
           `el.textContent = formatted` on `[data-product-price]` from
           wiping the badges out (D-0xx jawn PDP bug). */
        priceEl.textContent = formatMoney(variant.price);
        window.DP.applyPriceBadges(priceEl.parentElement, variant.price, variant.compare_at_price);
      }

      function updateForms(variant) {
        section.querySelectorAll('form[data-dp-atc-form], form[data-dp-express-form]').forEach((form) => {
          const idInput = form.querySelector('input[name="id"]');
          if (idInput && variant) idInput.value = variant.id;
        });
      }

      function updateAvailability(variant) {
        /* G2 (D-006, D-008, D-009): apply state to BOTH the main ATC
           button (scoped to this section) AND the sticky ATC button
           (page-level), so the sticky bar reflects sold-out state
           and can't proxy a click to a sold-out variant.

           Sold-out label includes the price ("Sold out • $34.99") on
           the main button to match the configurator UX (D-006). The
           sticky button stays compact with just "Sold out" since the
           sticky bar already shows the price in a separate element.

           Restore-on-available uses a regex test (not exact match) so
           it catches "Sold out", "Sold out • $X.XX", and "Unavailable"
           uniformly. Previously the exact-match check
           `textContent === 'Sold out'` silently failed once the price
           suffix was added, leaving the button stuck on "Sold out"
           even after the user clicked back to an in-stock variant
           (D-009 on Quefrency LM). */
        const mainAtc = section.querySelector('.dp-product__atc, button.btn--add-to-cart');
        const stickyAtc = document.querySelector('[data-dp-sticky-atc-button]');
        const buttons = [mainAtc, stickyAtc].filter(Boolean);
        if (buttons.length === 0) return;

        buttons.forEach((btn) => {
          const labelSpan = btn.querySelector('.btn__text') || btn;
          const isSticky = btn.hasAttribute('data-dp-sticky-atc-button');

          if (!variant) {
            btn.disabled = true;
            btn.setAttribute('aria-disabled', 'true');
            labelSpan.textContent = 'Unavailable';
            return;
          }

          if (variant.available === false) {
            btn.disabled = true;
            btn.setAttribute('aria-disabled', 'true');
            if (!isSticky) {
              const formatted = DP.formatMoney
                ? DP.formatMoney(variant.price)
                : '$' + (variant.price / 100).toFixed(2);
              labelSpan.textContent = 'Sold out • ' + formatted;
            } else {
              labelSpan.textContent = 'Sold out';
            }
            return;
          }

          // Available branch
          btn.disabled = false;
          btn.setAttribute('aria-disabled', 'false');
          if (/sold out|unavailable/i.test(labelSpan.textContent)) {
            const desiredLabel = btn.dataset.dpOriginalLabel
              || btn.dataset.originalLabel
              || 'Add to cart';
            labelSpan.textContent = desiredLabel;
          }
        });

        // Re-apply the running price suffix on the main ATC (non-configurator
        // products only — configurator path skips itself). Without this the
        // restore branch above leaves the main button on bare "Add to cart"
        // instead of "Add to cart • $X.XX" until the next qty/variant event.
        if (variant && variant.available !== false && DP.updateLineTotals) {
          DP.updateLineTotals();
        }
      }

      function updateUrl(variant) {
        if (!variant || !window.history || !window.history.replaceState) return;
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (err) { /* noop */ }
      }

      function formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          const fmt = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
          return window.Shopify.formatMoney(cents, fmt);
        }
        // Conservative fallback — assume USD with 2 decimals.
        return '$' + (cents / 100).toFixed(2);
      }

      /* Per-card price diff inside accordion bodies.
         Shopify variants are FULL option combinations, so we can't read
         each value's price in isolation. Instead, for every accordion-
         card (one per option-value), we compute:
            target_combo = [...currently_selected_options, but with
                            this card's option position replaced by the
                            card's value]
            diff = (target_variant.price - current_variant.price) cents
         Render "Included" when diff==0, "+$X.XX" / "-$X.XX" otherwise.
         Runs after every variant change so cross-option dependencies
         update too (e.g. picking a different switches makes case-card
         prices recompute against the new context). */
      function updatePriceDiffs() {
        const currentSelected = readSelected();
        const currentVariant = matchVariant(currentSelected);
        if (!currentVariant) return;
        picker.querySelectorAll('.dp-product__option.accordion__wrapper').forEach((wrapper) => {
          const pos = parseInt(wrapper.dataset.optionPosition, 10);
          if (!pos) return;
          wrapper.querySelectorAll('.radio__button').forEach((card) => {
            const input = card.querySelector('input[type="radio"]');
            if (!input) return;
            // Synthesize the target combination for this card.
            const target = currentSelected.slice();
            target[pos - 1] = input.value;
            const targetVariant = variants.find((v) => {
              const opts = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3];
              return target.every((val, i) => (opts[i] || null) === (val || null));
            });
            const col = card.querySelector('.variant_selectors');
            if (!col) return;
            /* `.variant_selectors` is flex-direction: column (see
               dp-theme.css ~3202), so direct children stack one per
               line. `.price_diff` + the save badge need to sit side by
               side, so they're nested one level deeper inside this row
               wrapper — a single column item that's itself a flex row. */
            let row = col.querySelector('.price_diff_row');
            if (!targetVariant) {
              if (row) row.remove();
              return;
            }
            if (!row) {
              row = document.createElement('span');
              row.className = 'price_diff_row';
              col.appendChild(row);
            }
            let diffEl = row.querySelector('.price_diff');
            const diffCents = targetVariant.price - currentVariant.price;
            if (!diffEl) {
              diffEl = document.createElement('span');
              diffEl.className = 'price_diff';
              row.appendChild(diffEl);
            }
            // "Included" reserved for the currently-selected card only.
            // Non-selected zero-diff cards show "+$0.00" — matches the
            // options-app's pattern where only the selected upsell shows
            // "Included" and equal-priced alternatives still show the
            // explicit "+0" so the buyer sees price parity, not absence.
            if (input.checked) {
              diffEl.textContent = 'Included';
            } else if (diffCents >= 0) {
              diffEl.textContent = '+' + formatMoney(diffCents);
            } else {
              diffEl.textContent = '-' + formatMoney(-diffCents);
            }

            /* This card's OWN variant can be on sale independently of
               whichever variant is currently selected elsewhere on the
               page (e.g. "Black Aluminum (A Stock)" below) — surface
               that as a fixed dollar "Save $X.XX" badge next to the
               price_diff text, reusing the PDP's save-pill styling. */
            let saveEl = row.querySelector('.price_diff__save');
            if (targetVariant.compare_at_price && targetVariant.compare_at_price > targetVariant.price) {
              const saveCents = targetVariant.compare_at_price - targetVariant.price;
              if (!saveEl) {
                saveEl = document.createElement('span');
                saveEl.className = 'price_diff__save dp-price__save';
                row.appendChild(saveEl);
              }
              saveEl.textContent = 'Sale - ' + formatMoney(saveCents) + ' Off';
            } else if (saveEl) {
              saveEl.remove();
            }
          });
        });
      }

      /* Coordinate the base-price with the Divine Power options-app's
         price aggregator. The app reads `data-base-price` ONCE at init
         (product-options.js line 664) and caches it on the instance as
         `el._po.basePrice` (dollars). When our variant picker swaps the
         underlying variant ID, we mirror the new base price into every
         `[data-product-options]._po` instance and call its public
         `updateGlobalPrice()` — otherwise the ATC button's running
         total stays frozen at the page-load variant's price.
         No-op on products without the options-app. */
      function syncOptionsAppBasePrice(variant) {
        if (!variant || typeof variant.price !== 'number') return;
        const dollars = variant.price / 100;
        document.querySelectorAll('[data-product-options]').forEach((mountEl) => {
          mountEl.dataset.basePrice = dollars.toFixed(2);
          const po = mountEl._po;
          if (po && typeof po.updateGlobalPrice === 'function') {
            po.basePrice = dollars;
            try { po.updateGlobalPrice(); } catch (e) { /* defensive */ }
          }
        });
      }

      function onChange() {
        /* D-002: capture qty before the variant-change cascade so we
           can restore it if anything below (most likely the options-
           app's `po.updateGlobalPrice()` triggered from
           syncOptionsAppBasePrice) re-renders the buy area and
           resets the qty input to its default. Sync restore catches
           synchronous resets; rAF restore catches late re-renders
           from event listeners subscribing to `variant:change`. */
        const savedQty = section.querySelector('[data-dp-qty-input]')?.value;

        const selected = readSelected();
        const variant = matchVariant(selected);
        updateLegendValues();
        updateRadioSelectedClass();
        updateOptionAvailability();
        updateForms(variant);
        updatePrice(variant);
        updateAvailability(variant);
        updateUrl(variant);
        syncOptionsAppBasePrice(variant);
        updatePriceDiffs();

        // Sync restore — re-query in case the input element itself was
        // replaced during the cascade (not just its value reset).
        if (savedQty) {
          const qNow = section.querySelector('[data-dp-qty-input]');
          if (qNow && qNow.value !== savedQty) qNow.value = savedQty;
        }

        if (window.DP && window.DP.updateLineTotals) window.DP.updateLineTotals();
        document.dispatchEvent(new CustomEvent('variant:change', {
          detail: { variant: variant, selected: selected },
          bubbles: true
        }));

        // Async restore — runs after subscribers to `variant:change`
        // have had a chance to react and potentially re-render.
        if (savedQty) {
          requestAnimationFrame(() => {
            const qLate = section.querySelector('[data-dp-qty-input]');
            if (qLate && qLate.value !== savedQty) {
              qLate.value = savedQty;
              if (window.DP && window.DP.updateLineTotals) window.DP.updateLineTotals();
            }
          });
        }
      }

      optionInputs.forEach((el) => el.addEventListener('change', onChange));
      // Init pass — apply currently-selected state.
      //
      // FIX (customer report, Aug 2026): previously this comment said "no
      // event dispatch since the page is already rendered correctly
      // server-side" — that assumption was wrong for the gallery image.
      // When a variant arrives pre-selected via ?variant=<id> in the URL,
      // the picker (radios/select) IS rendered correctly server-side, but
      // the main product image is NOT — it still shows the product's
      // default image, because the image-swap module (below, in this same
      // file) only reacts to the `variant:change` event and that event
      // was never fired on page load. Dispatching it here too fixes the
      // variant/image mismatch without touching the image-swap logic
      // itself. Safe to always dispatch: handleVariant() is idempotent,
      // so if the image is already correct this is a harmless no-op.
      updateRadioSelectedClass();
      updateOptionAvailability();
      updatePriceDiffs();
      {
        const initialSelected = readSelected();
        const initialVariant = matchVariant(initialSelected);
        if (initialVariant) {
          document.dispatchEvent(new CustomEvent('variant:change', {
            detail: { variant: initialVariant, selected: initialSelected },
            bubbles: true
          }));
        }
      }
      // Recompute line totals on init too (sticky ATC starts at qty=1
      // × initial unit price; this stays correct unless we explicitly
      // recompute after the unit-price element gets its first paint).
      if (window.DP && window.DP.updateLineTotals) window.DP.updateLineTotals();

      /* Accordion-card variant picker toggle.
         When option.values.size > 8 OR longest_value > 18 the picker
         renders accordion-cards instead of a <select> dropdown (see
         sections/dp-product.liquid `{%- if use_select -%}` branch).
         Markup mirrors the options-app's class names so styling at
         dp-theme.css lines 2514+ applies automatically. The
         options-app's own toggle JS is scoped to its own block
         container and doesn't discover our accordions — so we wire
         click → open/close here. Local single-open closes sibling
         accordions inside this picker; the cross-section enforcer
         at line ~930 handles coordination with options-app
         accordions on the same PDP.

         Slide animation uses height-from-scrollHeight measurement
         (~280ms ease-out). Matches the options-app's slideDown/slideUp
         visual subtlety. `transitionend` cleans up inline styles so
         subsequent transitions remeasure on each toggle (resilient
         to content changes between toggles). */
      function prefersReducedMotion() {
        return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      }
      function slideDown(el) {
        if (prefersReducedMotion()) {
          el.hidden = false;
          return;
        }
        el.hidden = false;
        el.style.height = '0px';
        el.style.overflow = 'hidden';
        el.style.transition = 'none';
        void el.offsetHeight; // flush
        const target = el.scrollHeight;
        el.style.transition = 'height 280ms cubic-bezier(0.4, 0, 0.2, 1)';
        el.style.height = target + 'px';
        const cleanup = () => {
          el.style.height = '';
          el.style.overflow = '';
          el.style.transition = '';
          el.removeEventListener('transitionend', cleanup);
        };
        el.addEventListener('transitionend', cleanup, { once: true });
        setTimeout(cleanup, 360); // safety fallback
      }
      function slideUp(el) {
        if (el.hidden) return;
        if (prefersReducedMotion()) {
          el.hidden = true;
          return;
        }
        el.style.height = el.scrollHeight + 'px';
        el.style.overflow = 'hidden';
        el.style.transition = 'none';
        void el.offsetHeight;
        el.style.transition = 'height 240ms cubic-bezier(0.4, 0, 0.2, 1)';
        el.style.height = '0px';
        const cleanup = () => {
          el.hidden = true;
          el.style.height = '';
          el.style.overflow = '';
          el.style.transition = '';
          el.removeEventListener('transitionend', cleanup);
        };
        el.addEventListener('transitionend', cleanup, { once: true });
        setTimeout(cleanup, 320);
      }
      picker.querySelectorAll('.accordion__wrapper').forEach((wrapper) => {
        const title = wrapper.querySelector('.accordion__title');
        const body = wrapper.querySelector('.accordion__body');
        if (!title || !body) return;
        title.addEventListener('click', () => {
          const isOpen = title.classList.contains('accordion-is-open');
          if (isOpen) {
            title.classList.remove('accordion-is-open');
            title.setAttribute('aria-expanded', 'false');
            slideUp(body);
            return;
          }
          // Single-open within this picker: close any sibling accordion.
          picker.querySelectorAll('.accordion__wrapper').forEach((w) => {
            if (w === wrapper) return;
            const t = w.querySelector('.accordion__title');
            const b = w.querySelector('.accordion__body');
            if (t && t.classList.contains('accordion-is-open')) {
              t.classList.remove('accordion-is-open');
              t.setAttribute('aria-expanded', 'false');
              if (b) slideUp(b);
            }
          });
          title.classList.add('accordion-is-open');
          title.setAttribute('aria-expanded', 'true');
          slideDown(body);
        });
      });
    });

    /* Manage .is-selected class for sibling-style radios/swatches/rate cards
       (replaces expensive CSS :has() selectors) — kept from old module. */
    document.addEventListener('change', (e) => {
      const radio = e.target;
      if (!radio || radio.type !== 'radio') return;
      const wrapper = radio.closest('.dp-product__swatch, .dp-rate-card');
      if (!wrapper) return;
      const group = radio.name;
      document.querySelectorAll(`input[name="${group}"]`).forEach((r) => {
        const w = r.closest('.dp-product__swatch, .dp-rate-card');
        if (w) w.classList.toggle('is-selected', r.checked);
      });
    });
    document.querySelectorAll('.dp-product__swatch input:checked, .dp-rate-card input:checked').forEach((r) => {
      r.closest('.dp-product__swatch, .dp-rate-card')?.classList.add('is-selected');
    });
  });
})();

/* ============================================================
   dp-faq
   ============================================================ */
(function () {
  window.DP.register('faq', function () {
    document.querySelectorAll('.dp-faq-section, .dp-specs-accordion-section').forEach((section) => {
      const single = section.dataset.dpSingle === 'true';
      // a11y: collapsed answers are inert so their links leave the Tab
      // order, and the question announces its expanded state.
      const syncItem = (it) => {
        const isOpen = it.classList.contains('is-open');
        const a = it.querySelector('.dp-faq__answer');
        const qq = it.querySelector('.dp-faq__question');
        if (a) { a.setAttribute('aria-hidden', !isOpen); a.inert = !isOpen; }
        if (qq) qq.setAttribute('aria-expanded', isOpen);
      };
      section.querySelectorAll('.dp-faq__item').forEach((item) => {
        const q = item.querySelector('.dp-faq__question');
        syncItem(item);
        q?.addEventListener('click', () => {
          if (single) {
            section.querySelectorAll('.dp-faq__item.is-open').forEach((other) => {
              if (other !== item) { other.classList.remove('is-open'); syncItem(other); }
            });
          }
          item.classList.toggle('is-open');
          syncItem(item);
        });
        q?.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); q.click(); }
        });
      });
    });
  });
})();

/* ============================================================
   youtube-lazy
   Convert .youtube-player[data-id] placeholders into a thumbnail
   + play-button overlay. Click loads the iframe (autoplay). Used
   in product descriptions (e.g. Repair Service) where the merchant
   embeds videos via the legacy `<div class="youtube-player"
   data-id="..."></div>` markup. Matches the behavior of the legacy
   theme handler in keebio-current-original/assets/custom.js so
   existing description HTML keeps working without re-editing.
   Lazy loading keeps page weight low — iframes are 600KB+ each
   before any user interaction.
   ============================================================ */
(function () {
  window.DP.register('youtube-lazy', function () {
    document.querySelectorAll('.youtube-player[data-id]').forEach((el) => {
      const id = el.dataset.id;
      if (!id || el.dataset.dpInit === '1') return;
      el.dataset.dpInit = '1';
      el.innerHTML = '';
      const thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = 'youtube-player__thumb';
      thumb.setAttribute('aria-label', 'Play video');
      thumb.style.backgroundImage = 'url(https://img.youtube.com/vi/' + id + '/hqdefault.jpg)';
      thumb.innerHTML = '<span class="youtube-player__play" aria-hidden="true">▶</span>';
      thumb.addEventListener('click', function () {
        const iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0';
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        el.innerHTML = '';
        el.appendChild(iframe);
      });
      el.appendChild(thumb);
    });
  });
})();

/* ============================================================
   dp-product-options-summaries
   For each mother accordion in .dp-product-section:
     - Inject a selected-option line inside every
       .product-options__subgroup-header (e.g. "Keycaps" shows "None").
     - Inject a combined summary as a sibling of .accordion__title
       listing all variant_type selections. CSS hides it when the
       accordion is open so it only appears in the closed state.
   ============================================================ */
(function () {
  function getSelectedLabel(fieldset) {
    if (!fieldset) return '';
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (!checked) return '';
    const container = checked.closest('.radio__button') || checked.parentElement;
    if (!container) return '';
    const lbl = container.querySelector('label') || container;

    // Try structured spans first.
    const title   = (lbl.querySelector('.upsell-product-title')?.textContent || '').trim();
    const variant = (lbl.querySelector('.upsells__variant__name')?.textContent || '').trim();
    if (title && variant) return title + ' | ' + variant;
    if (title || variant) return title || variant;

    // Fallback: strip price_diff / badges / images, take whatever text remains.
    const clone = lbl.cloneNode(true);
    clone.querySelectorAll(
      '.price_diff, .incompatible-badge, img, picture, .upsell__image_wrapper, [class*="image"]'
    ).forEach((n) => n.remove());
    return (clone.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function nextFieldset(el) {
    let s = el.nextElementSibling;
    while (s && !s.matches('.radio__fieldset')) s = s.nextElementSibling;
    return s;
  }

  function updateSubgroupSummaries(root) {
    root.querySelectorAll('.product-options__subgroup-header').forEach((header) => {
      const val = getSelectedLabel(nextFieldset(header));
      let span = header.querySelector('.dp-subgroup-selected');
      if (!span) {
        span = document.createElement('span');
        span.className = 'dp-subgroup-selected';
        header.appendChild(span);
      }
      if (span.textContent !== val) span.textContent = val;
    });
  }

  function isAccordionOpen(wrapper) {
    // Trust the title's own class first — the app toggles this reliably.
    const title = wrapper.querySelector('.accordion__title');
    if (title) {
      if (title.classList.contains('accordion-is-open')) return true;
      if (title.getAttribute('aria-expanded') === 'true') return true;
      return false; // title exists and says closed → believe it
    }
    // Fallback only if the title isn't found for some reason.
    const cb = wrapper.querySelector('input[type="checkbox"]');
    if (cb && cb.checked) return true;
    const fs = wrapper.querySelector('.radio__fieldset');
    if (fs && fs.offsetParent !== null && fs.offsetHeight > 0) return true;
    return false;
  }

  function updateMotherSummaries(root) {
    root.querySelectorAll('.accordion__wrapper').forEach((wrapper) => {
      const title = wrapper.querySelector('.accordion__title');
      const body  = wrapper.querySelector('.accordion__body');
      if (!title || !body) return;
      const parts = [];
      body.querySelectorAll('.product-options__subgroup-header').forEach((header) => {
        const labelText = (header.querySelector('.radio__legend__label')?.textContent || '').trim();
        const val = getSelectedLabel(nextFieldset(header));
        if (labelText && val) parts.push(labelText + ': ' + val);
      });
      let summary = wrapper.querySelector(':scope > .dp-mother-summary');
      if (!summary) {
        summary = document.createElement('div');
        summary.className = 'dp-mother-summary';
        wrapper.insertBefore(summary, body);
      }
      const text = parts.join(' · ');
      if (summary.textContent !== text) summary.textContent = text;
      // Hide when the accordion is open (the subgroup lines cover the same info).
      const open = isAccordionOpen(wrapper);
      if (summary.hidden !== open) summary.hidden = open;
    });
  }

  let scheduled = false;
  function updateAll() {
    document.querySelectorAll('.dp-product-section').forEach((root) => {
      updateSubgroupSummaries(root);
      updateMotherSummaries(root);
    });
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; updateAll(); });
  }

  function init() {
    updateAll();
    // The app may re-render after initial paint; catch it.
    setTimeout(updateAll, 300);
    setTimeout(updateAll, 800);
    setTimeout(updateAll, 2000);
    setTimeout(updateAll, 4000);

    // Catch native change events and app-level click events on labels.
    document.addEventListener('change', (e) => {
      if (e.target.matches && e.target.matches('.dp-product-section input[type="radio"]')) schedule();
    });
    document.addEventListener('click', (e) => {
      if (!e.target.closest) return;
      if (e.target.closest('.dp-product-section .radio__button')) schedule();
      // Accordion open/close toggles — needed to show/hide the mother summary.
      if (e.target.closest('.dp-product-section .accordion__title')) {
        schedule();
        // Re-check on the next frames in case the app animates open/close.
        setTimeout(updateAll, 100);
        setTimeout(updateAll, 400);
      }
    }, true);

    // Watch for app-driven DOM swaps (e.g. sibling variant switch re-renders).
    const roots = document.querySelectorAll('.dp-product-section');
    if (roots.length && 'MutationObserver' in window) {
      const mo = new MutationObserver((muts) => {
        // Ignore mutations we caused (adding/removing our own summary nodes).
        const relevant = muts.some((m) =>
          Array.from(m.addedNodes).some((n) =>
            n.nodeType === 1 && !n.classList?.contains('dp-subgroup-selected')
              && !n.classList?.contains('dp-mother-summary')
          )
        );
        if (relevant) schedule();
      });
      roots.forEach((r) => mo.observe(r, { childList: true, subtree: true }));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ── dp-reviews bar chart: hydrate from Judge.me histogram ── */
(function () {
  function hydrate() {
    const histogram = document.querySelector('.jdgm-histogram');
    const chart = document.querySelector('.dp-reviews-bar-chart');
    if (!histogram || !chart) return;

    const rows = histogram.querySelectorAll('.jdgm-histogram__row');
    if (!rows.length) return;

    rows.forEach(function (row) {
      const star  = row.getAttribute('data-rating');
      const freqEl = row.querySelector('.jdgm-histogram__frequency');
      const barEl  = row.querySelector('.jdgm-histogram__bar-content');
      if (!star || !freqEl || !barEl) return;

      const count = freqEl.textContent.trim();
      const pct   = parseFloat(barEl.style.width) || 0;

      const dpBar = chart.querySelector('[data-star="' + star + '"]');
      if (!dpBar) return;
      dpBar.querySelector('.dp-review-bar__fill').style.width = pct + '%';
      dpBar.querySelector('.dp-review-bar__count').textContent = count;
    });

    document.querySelectorAll('.jdgm-row-stars').forEach(function (el) {
      el.style.display = 'none';
    });
  }

  document.addEventListener('jdgm.doneSetup', hydrate);

  /* already done, or wait for histogram to appear */
  if (window.jdgm && window.jdgm._doneSetup) {
    hydrate();
  } else {
    var _obs = new MutationObserver(function () {
      if (document.querySelector('.jdgm-histogram')) {
        _obs.disconnect();
        hydrate();
      }
    });
    _obs.observe(document.body, { childList: true, subtree: true });
  }
})();

/* ── dp-reviews write-review button: delegate to Judge.me's hidden link ── */
(function () {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.dp-reviews__write-review-btn');
    if (!btn) return;
    e.preventDefault();
    var link = document.querySelector('.jdgm-write-rev-link');
    if (link) {
      link.click();
    }
  });
})();

/* ============================================================
   dp-product-options-single-open
   The Divine Power product-options-app renders its own accordions
   inside .dp-product-section. Force single-open behavior: when one
   header is clicked, close any sibling accordions that are open.
   Uses the app's existing toggle class `accordion-is-open`.
   ============================================================ */
(function () {
  document.addEventListener('click', function (e) {
    const title = e.target.closest('.dp-product-section .accordion__title');
    if (!title) return;
    // Let the app's own handler run first, then reconcile state.
    requestAnimationFrame(function () {
      const section = title.closest('.dp-product-section');
      if (!section) return;
      if (!title.classList.contains('accordion-is-open')) return; // just closed — do nothing
      section.querySelectorAll('.accordion__title.accordion-is-open').forEach(function (t) {
        if (t !== title) t.click();
      });
    });
  }, true);
})();

/* ============================================================
   dp-sticky-atc
   Show the sticky ATC once the primary buy box has scrolled far enough
   out of view. Uses IntersectionObserver on an anchor when available;
   falls back to a scroll-position check tied to .dp-product__info so
   it still works even if the ATC button is inside the product-options
   app block (which would otherwise make the observer's target unstable).
   ============================================================ */
(function () {
  window.DP.register('sticky-atc', function () {
    const sticky = document.querySelector('.dp-sticky-atc-section');
    if (!sticky) return;
    const atc    = document.querySelector('.dp-product__atc');
    const info   = document.querySelector('.dp-product__info');

    /* ----------------------------------------------------------
       Submission proxy (R2 P0-1B).
       Sticky ATC button has type="button" and no form. On click
       we find the regular ATC form (form[data-dp-atc-form] on
       dp-product.liquid's buy box) and call requestSubmit() so
       the configurator app's fetch-patch sees all hidden inputs
       from the configurator state. Without this proxy the sticky
       button would either do nothing (no form) or submit only the
       base variant id (dropping configurator selections).
       ---------------------------------------------------------- */
    const stickyBtn = sticky.querySelector('[data-dp-sticky-atc-button]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (stickyBtn.disabled) return;
        const atcForm = document.querySelector('form[data-dp-atc-form]');
        if (!atcForm) {
          console.warn('[dp-sticky-atc] no form[data-dp-atc-form] on page; sticky ATC cannot proxy submission');
          return;
        }
        // Visual feedback: disable briefly to debounce double-taps.
        stickyBtn.disabled = true;
        const original = stickyBtn.textContent;
        stickyBtn.textContent = 'Adding…';
        // Submit the regular form. requestSubmit triggers HTML5
        // validation (so required-option <select required> errors
        // surface), then dispatches a submit event the page's
        // existing handlers (configurator app, payment-interceptor)
        // already listen to.
        try {
          if (typeof atcForm.requestSubmit === 'function') {
            atcForm.requestSubmit();
          } else {
            atcForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }
        } finally {
          // Re-enable after a short delay so successive taps
          // can fire on different products.
          setTimeout(function () {
            stickyBtn.disabled = false;
            stickyBtn.textContent = original;
          }, 1200);
        }
      });
    }

    function setVisible(on) { sticky.classList.toggle('is-visible', !!on); }

    // Preferred: observe the native ATC button (stable across app re-renders
    // because the button is inside dp-product.liquid, not the app's slot).
    if (atc && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(function(entries) {
        setVisible(!entries[0].isIntersecting);
      }, { threshold: 0 });
      io.observe(atc);
      return;
    }

    // Fallback: show once the user has scrolled past 60% of the info panel.
    if (info) {
      const onScroll = window.DP.debounce(function() {
        const rect = info.getBoundingClientRect();
        const threshold = rect.height * 0.6;
        setVisible(rect.bottom < threshold);
      }, 30);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  });
})();

/* ============================================================
   dp-cross-sells — inline ATC + bulk add-all-to-cart
   ============================================================ */
(function () {
  window.DP.register('cross-sells', function () {
    /* ----------------------------------------------------------
       Shopify recommendations fetch (Tier 2.2).
       Tracks marked [data-dp-recommendations-url] are skeletons on
       initial PDP render. We fetch the section-rendering endpoint,
       extract the replacement track's inner HTML, and swap. If the
       response has no products, we hide the whole section so the
       page doesn't show an empty "Frequently Bought Together" band.
       ---------------------------------------------------------- */
    document.querySelectorAll('[data-dp-recommendations-url]').forEach(function(track) {
      if (track.dataset.dpRecommendationsFetched === 'true') return;
      track.dataset.dpRecommendationsFetched = 'true';
      const url = track.dataset.dpRecommendationsUrl;
      const section = track.closest('.dp-cross-sells-section');
      fetch(url, { headers: { 'Accept': 'text/html' } })
        .then(function(r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function(html) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newTrack = doc.querySelector('[data-dp-scroll-track]');
          if (newTrack && newTrack.children.length > 0) {
            track.innerHTML = newTrack.innerHTML;
            // Re-run reveal animations on any new cards that opt in.
            if (window.DP && typeof window.DP.initReveals === 'function') {
              try { window.DP.initReveals(track); } catch (e) {}
            }
            // Re-evaluate scroller arrow state now that real content sits in the track.
            track.dispatchEvent(new Event('scroll'));
          } else if (section) {
            section.style.display = 'none';
          }
        })
        .catch(function(err) {
          console.warn('[dp-cross-sells] recommendations fetch failed:', err);
          if (section) section.style.display = 'none';
        });
    });

    // Inline ATC per card: intercept form submit, add via cart AJAX, open mini-cart.
    document.querySelectorAll('form[data-dp-inline-atc]').forEach(function(form) {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        const btn = form.querySelector('button[type="submit"]');
        const idInput = form.querySelector('input[name="id"]');
        if (!idInput) return;
        const original = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
        window.DP.cart.add({ id: parseInt(idInput.value, 10), quantity: 1 })
          .then(function() {
            if (btn) btn.textContent = 'Added ✓';
            // Open mini-cart if present so customer sees the result.
            const toggle = window.DP.actions['toggle-mini-cart'];
            if (toggle) setTimeout(toggle, 250);
            setTimeout(function() {
              if (btn) { btn.disabled = false; btn.textContent = original; }
            }, 1400);
          })
          .catch(function() {
            if (btn) { btn.disabled = false; btn.textContent = 'Try again'; }
            setTimeout(function() {
              if (btn) btn.textContent = original;
            }, 2000);
          });
      });
    });

    // Bulk "Add all to cart" — takes a comma-separated list of variant IDs.
    window.DP.bindAction('add-all-fbt', function(el) {
      const ids = (el.getAttribute('data-fbt-ids') || '').split(',').map(function(s) { return parseInt(s.trim(), 10); }).filter(Boolean);
      if (!ids.length) return;
      const original = el.textContent;
      el.disabled = true;
      el.textContent = 'Adding all…';
      window.DP.cart.add(ids.map(function(id) { return { id: id, quantity: 1 }; }))
        .then(function() {
          el.textContent = 'Added ✓';
          const toggle = window.DP.actions['toggle-mini-cart'];
          if (toggle) setTimeout(toggle, 300);
          setTimeout(function() { el.disabled = false; el.textContent = original; }, 1600);
        })
        .catch(function() {
          el.disabled = false; el.textContent = 'Try again';
          setTimeout(function() { el.textContent = original; }, 2000);
        });
    });
  });
})();

/* ============================================================
   dp-product-video — open the Watch Video overlay
   ============================================================ */
(function () {
  window.DP.register('product-video', function () {
    window.DP.bindAction('open-product-video', function(el) {
      const url = el.getAttribute('data-video-url');
      if (!url) return;
      const embed = toEmbed(url);
      const modal = document.createElement('div');
      modal.className = 'dp-product__video-modal';
      modal.innerHTML =
        '<div class="dp-product__video-backdrop" data-dp-close></div>' +
        '<div class="dp-product__video-panel">' +
          '<button class="dp-product__video-close" data-dp-close aria-label="Close">&#x2715;</button>' +
          '<div class="dp-product__video-embed">' +
            (embed
              ? '<iframe src="' + embed + '" allow="autoplay; fullscreen" allowfullscreen loading="lazy"></iframe>'
              : '<video src="' + url + '" controls autoplay playsinline></video>') +
          '</div>' +
        '</div>';
      modal.addEventListener('click', function(e) {
        if (e.target.closest('[data-dp-close]')) modal.remove();
      });
      document.body.appendChild(modal);
    });

    function toEmbed(url) {
      const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
      if (yt) return 'https://www.youtube.com/embed/' + yt[1] + '?autoplay=1&rel=0';
      const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (vm) return 'https://player.vimeo.com/video/' + vm[1] + '?autoplay=1';
      return null;
    }
  });
})();

/* ============================================================
   dp-product-gallery
   ============================================================ */
(function () {
  window.DP.register('product-gallery', function () {
    const main = document.querySelector('[data-dp-main-image]');
    const thumbs = Array.from(document.querySelectorAll('[data-dp-thumbs] [data-dp-thumb-index]'));
    if (!main || thumbs.length === 0) return;
    const mainImg = main.querySelector('img');
    if (!mainImg) return;

    // Resize any Shopify CDN URL to a target width by rewriting the
    // `width=` query param (handles both `?width=` and `&width=`).
    function resizeCdnUrl(url, w) {
      if (!url) return url;
      try {
        const u = new URL(url, window.location.origin);
        if (u.searchParams.has('width')) {
          u.searchParams.set('width', String(w));
          return u.toString();
        }
        u.searchParams.set('width', String(w));
        return u.toString();
      } catch (e) {
        return url.replace(/([?&])width=\d+/, '$1width=' + w);
      }
    }

    function setActive(thumb) {
      thumbs.forEach(x => x.classList.remove('is-active'));
      if (thumb) thumb.classList.add('is-active');
    }

    function swapMainTo(src, alt) {
      if (!src) return;
      // Clear srcset so the browser doesn't keep the old candidate.
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');
      mainImg.src = src;
      if (alt) mainImg.alt = alt;
    }

    // Thumbnail click — use the pre-rendered 1400w URL from the data attr.
    thumbs.forEach((t) => {
      t.addEventListener('click', () => {
        setActive(t);
        const bigSrc = t.dataset.dpImageSrc
          || resizeCdnUrl(t.querySelector('img')?.currentSrc || t.querySelector('img')?.src, 1400);
        swapMainTo(bigSrc, t.dataset.dpImageAlt);
      });
    });

    // Variant-change → swap main image when a swatch/sibling pick carries one.
    // Matches by image id if available, otherwise by URL substring.
    function findThumbForVariant(variant) {
      if (!variant) return null;
      const imgId = variant.featured_image?.id || variant.featured_media?.preview_image?.id;
      if (imgId) {
        const byId = thumbs.find(t => String(t.dataset.dpImageId) === String(imgId));
        if (byId) return byId;
      }
      const url = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
      if (url) {
        const base = url.split('?')[0];
        return thumbs.find(t => (t.dataset.dpImageSrc || '').split('?')[0] === base) || null;
      }
      return null;
    }

    function handleVariant(variant) {
      const t = findThumbForVariant(variant);
      if (t) { setActive(t); swapMainTo(t.dataset.dpImageSrc, t.dataset.dpImageAlt); return; }
      // Fallback: if variant has an image URL but no matching thumb, use it directly.
      const url = variant?.featured_image?.src || variant?.featured_media?.preview_image?.src;
      if (url) swapMainTo(resizeCdnUrl(url, 1400));
    }

    // Shopify native event
    document.addEventListener('variant:change', (e) => handleVariant(e.detail?.variant));
    // Divine Power app / generic events
    document.addEventListener('product:variant-change', (e) => handleVariant(e.detail?.variant || e.detail));
    document.addEventListener('on:variant:change', (e) => handleVariant(e.detail?.variant));

    // Fallback: watch the hidden variant id input for changes (covers apps
    // that don't dispatch an event). When the id changes, look it up in
    // window product JSON if available.
    const idInput = document.querySelector('.dp-product__form input[name="id"]');
    if (idInput) {
      let last = idInput.value;
      const poll = setInterval(() => {
        if (idInput.value === last) return;
        last = idInput.value;
        const pj = window.__dpProductJson || window.ShopifyAnalytics?.meta?.product;
        if (pj && Array.isArray(pj.variants)) {
          const v = pj.variants.find(x => String(x.id) === String(last));
          if (v) handleVariant(v);
        }
      }, 400);
      window.addEventListener('beforeunload', () => clearInterval(poll));
    }

    // --- Mobile swipe on the main image (carousel-track feel) ----------
    // Wraps the main image in a 3-slot track [prev | current | next] that
    // translates together. User drags and sees the adjacent image come in
    // — no grey gap. On release, snap to nearest slot; if direction changes,
    // swap sources and re-center.
    const coarse = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    if (coarse && main && mainImg && thumbs.length > 1) {
      // Flex centering (set in CSS for desktop still-image centering) conflicts
      // with the 300%-wide carousel track — reset to block layout.
      main.style.display = 'block';
      // Build the track once.
      const track = document.createElement('div');
      track.className = 'dp-product__track';
      track.style.cssText = 'display:flex; width:300%; height:100%; transform:translateX(-33.3333%); will-change:transform;';

      const slotStyle = 'flex:0 0 33.3333%; height:100%; display:flex; align-items:center; justify-content:center;';
      const prevSlot = document.createElement('div');
      const currSlot = document.createElement('div');
      const nextSlot = document.createElement('div');
      prevSlot.style.cssText = slotStyle;
      currSlot.style.cssText = slotStyle;
      nextSlot.style.cssText = slotStyle;

      // Move the real mainImg into the middle slot.
      const originalParent = mainImg.parentNode;
      currSlot.appendChild(mainImg);
      const imgStyle = 'max-width:100%; max-height:100%; width:auto; height:auto; display:block; border-radius:12px;';
      mainImg.style.cssText = (mainImg.getAttribute('style') || '') + ';' + imgStyle;

      // Create adjacent images (same classes for CSS).
      const prevImg = document.createElement('img');
      const nextImg = document.createElement('img');
      prevImg.className = mainImg.className;
      nextImg.className = mainImg.className;
      prevImg.alt = ''; nextImg.alt = '';
      // Eager + sync decode: these must be ready the instant a drag starts,
      // otherwise the empty slot shows as grey container background until
      // the fetch resolves.
      prevImg.decoding = 'sync'; nextImg.decoding = 'sync';
      prevImg.loading = 'eager'; nextImg.loading = 'eager';
      prevImg.fetchPriority = 'high'; nextImg.fetchPriority = 'high';
      prevImg.draggable = false; nextImg.draggable = false;
      prevImg.style.cssText = imgStyle;
      nextImg.style.cssText = imgStyle;
      prevSlot.appendChild(prevImg);
      nextSlot.appendChild(nextImg);

      track.appendChild(prevSlot);
      track.appendChild(currSlot);
      track.appendChild(nextSlot);

      // main is `position: sticky`; append track inside it. Ensure main
      // clips (already has overflow:hidden from CSS).
      originalParent.appendChild(track);

      function currentIndex() {
        const i = thumbs.findIndex(t => t.classList.contains('is-active'));
        return i >= 0 ? i : 0;
      }
      function srcFor(i) {
        if (i < 0 || i >= thumbs.length) return '';
        return thumbs[i].dataset.dpImageSrc || '';
      }
      function refreshAdjacent() {
        const i = currentIndex();
        const ps = srcFor(i - 1);
        const ns = srcFor(i + 1);
        if (ps) { prevImg.src = ps; prevImg.style.visibility = 'visible'; }
        else { prevImg.removeAttribute('src'); prevImg.style.visibility = 'hidden'; }
        if (ns) { nextImg.src = ns; nextImg.style.visibility = 'visible'; }
        else { nextImg.removeAttribute('src'); nextImg.style.visibility = 'hidden'; }
      }
      refreshAdjacent();

      // Re-populate adjacents whenever the active thumb changes (click or swipe).
      const thumbObserver = new MutationObserver(() => refreshAdjacent());
      thumbs.forEach(t => thumbObserver.observe(t, { attributes: true, attributeFilter: ['class'] }));

      let startX = 0, startY = 0, tracking = false, axisLocked = null, width = 0;
      const THRESH_RATIO = 0.18;  // fraction of width to commit
      const MIN_PX = 40;

      function setTrack(x, animate) {
        track.style.transition = animate
          ? 'transform 280ms cubic-bezier(.22,.61,.36,1)'
          : 'none';
        track.style.transform = `translateX(calc(-33.3333% + ${x}px))`;
      }

      main.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        tracking = true;
        axisLocked = null;
        startX = e.clientX;
        startY = e.clientY;
        width = main.getBoundingClientRect().width;
        setTrack(0, false);
      });

      main.addEventListener('pointermove', (e) => {
        if (!tracking) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (axisLocked === null) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          axisLocked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (axisLocked === 'x') {
            main.style.touchAction = 'none';
            try { main.setPointerCapture(e.pointerId); } catch (_) {}
          } else {
            tracking = false;
            return;
          }
        }
        if (axisLocked !== 'x') return;

        // Rubber-band at the ends when no prev/next image exists.
        const i = currentIndex();
        let follow = dx;
        if (dx > 0 && i === 0) follow = dx * 0.3;
        if (dx < 0 && i === thumbs.length - 1) follow = dx * 0.3;
        setTrack(follow, false);
      });

      function end(e) {
        if (!tracking) return;
        tracking = false;
        main.style.touchAction = 'pan-y';
        if (axisLocked !== 'x') return;

        const dx = (e && typeof e.clientX === 'number') ? (e.clientX - startX) : 0;
        const thresh = Math.max(MIN_PX, width * THRESH_RATIO);
        const i = currentIndex();
        const wantsNext = dx < -thresh && i < thumbs.length - 1;
        const wantsPrev = dx >  thresh && i > 0;

        if (!wantsNext && !wantsPrev) {
          setTrack(0, true);
          return;
        }

        const targetDx = wantsNext ? -width : width;
        setTrack(targetDx, true);

        const finalize = () => {
          track.removeEventListener('transitionend', finalize);
          // Swap active thumb (which updates mainImg.src via the click handler).
          const nextIdx = wantsNext ? i + 1 : i - 1;
          thumbs[nextIdx].click();
          // Reset track to center (the new current is now in the middle slot
          // because mainImg's src was updated in place).
          setTrack(0, false);
          refreshAdjacent();
        };
        track.addEventListener('transitionend', finalize, { once: true });
        setTimeout(() => { if (tracking === false) finalize(); }, 320);
      }

      main.addEventListener('pointerup', end);
      main.addEventListener('pointercancel', () => {
        tracking = false;
        main.style.touchAction = 'pan-y';
        setTrack(0, true);
      });

      // Prevent iOS long-press save
      main.style.touchAction = 'pan-y';
      main.querySelectorAll('img').forEach(i => i.setAttribute('draggable', 'false'));
    }

    // --- Image zoom lightbox -------------------------------------------
    // Swipes suppress click via setPointerCapture, so genuine taps open zoom on mobile too.
    if (!coarse) mainImg.style.cursor = 'zoom-in';

    {
      function openZoom() {
        const originRect = mainImg.getBoundingClientRect();
        const src = mainImg.currentSrc || mainImg.src;
        const alt = mainImg.alt;

        const modal = document.createElement('div');
        modal.className = 'dp-img-zoom-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.innerHTML =
          '<div class="dp-img-zoom-backdrop" data-dp-close></div>' +
          '<button class="dp-img-zoom-close" data-dp-close aria-label="Close">&#x2715;</button>' +
          '<img class="dp-img-zoom-img" data-dp-close src="' + src + '" alt="' + alt.replace(/"/g, '&quot;') + '">';
        modal.addEventListener('click', (e) => {
          if (e.target.closest('[data-dp-close]') || e.target === modal) closeZoom(modal);
        });
        document.body.appendChild(modal);

        const zoomImg = modal.querySelector('.dp-img-zoom-img');

        // Compute final displayed size from natural dimensions
        const pad = 48;
        const maxW = window.innerWidth - pad;
        const maxH = window.innerHeight - pad;
        const natW = mainImg.naturalWidth || originRect.width;
        const natH = mainImg.naturalHeight || originRect.height;
        const fit = Math.min(maxW / natW, maxH / natH, 1);
        const finalW = natW * fit;
        const finalH = natH * fit;

        // FLIP: offset from final center to origin center, scale from final to origin size
        const dx = (originRect.left + originRect.width / 2) - window.innerWidth / 2;
        const dy = (originRect.top + originRect.height / 2) - window.innerHeight / 2;
        const scale = Math.min(originRect.width / finalW, originRect.height / finalH);

        // Start at origin — no transition
        zoomImg.style.transition = 'none';
        zoomImg.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
        zoomImg.getBoundingClientRect(); // force reflow

        // Animate to final
        zoomImg.style.transition = 'transform 380ms cubic-bezier(0.22, 1, 0.36, 1)';
        zoomImg.style.transform = '';
        modal.classList.add('is-open');

        // Store for zoom-out on close
        modal._zoomOut = () => {
          zoomImg.style.transition = 'transform 300ms cubic-bezier(0.4, 0, 1, 1), opacity 250ms ease';
          zoomImg.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
          zoomImg.style.opacity = '0';
        };

        function onKey(e) {
          if (e.key === 'Escape') { closeZoom(modal); document.removeEventListener('keydown', onKey); }
        }
        document.addEventListener('keydown', onKey);
        modal._removeKey = () => document.removeEventListener('keydown', onKey);
      }

      function closeZoom(modal) {
        if (modal._removeKey) modal._removeKey();
        if (modal._zoomOut) modal._zoomOut();
        modal.classList.remove('is-open');
        const zoomImg = modal.querySelector('.dp-img-zoom-img');
        (zoomImg || modal).addEventListener('transitionend', () => modal.remove(), { once: true });
      }

      mainImg.addEventListener('click', openZoom);
    }
  });
})();


/* ============================================================
   collection-toolbar
   ============================================================ */
(function () {
  window.DP.register('collection-filters', function () {
    // Sidebar + layout refs.
    const sidebar  = document.querySelector('[data-dp-collection-sidebar]');
    const backdrop = document.querySelector('.dp-collection-sidebar__backdrop');
    const layout   = document.querySelector('.dp-collection-grid-section .dp-collection-layout');
    const filterBtn = document.querySelector('[data-dp-action="open-dp-filters"]');

    function isMobile() { return window.matchMedia('(max-width: 899px)').matches; }

    // Mobile drawer open/close.
    function openMobileDrawer() {
      if (!sidebar) return;
      sidebar.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
    function closeMobileDrawer() {
      if (!sidebar) return;
      sidebar.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    // Desktop sidebar hide/show — collapses the grid column to 0
    // (managed via `.is-filters-hidden` on the layout wrapper). State
    // persists in localStorage so the preference survives navigation.
    function setDesktopHidden(hidden) {
      if (!layout) return;
      layout.classList.toggle('is-filters-hidden', hidden);
      if (filterBtn) filterBtn.setAttribute('aria-expanded', hidden ? 'false' : 'true');
      localStorage.setItem('dp-filters-hidden', hidden ? '1' : '0');
    }
    // Restore persisted state on load.
    if (!isMobile() && localStorage.getItem('dp-filters-hidden') === '1') {
      setDesktopHidden(true);
    }

    // Single action handler — behaves differently per viewport.
    window.DP.bindAction('open-dp-filters', () => {
      if (isMobile()) {
        openMobileDrawer();
      } else if (layout) {
        setDesktopHidden(!layout.classList.contains('is-filters-hidden'));
      }
    });
    window.DP.bindAction('close-dp-filters', closeMobileDrawer);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sidebar && sidebar.classList.contains('is-open')) closeMobileDrawer();
    });

    // Filter checkboxes + price inputs → submit the filter form (which
    // preserves sort_by via a hidden input). Checkboxes submit
    // immediately; price is debounced + commits on Enter. requestSubmit()
    // (not submit()) so the submit listener below runs.
    const filterForm = document.querySelector('[data-dp-filter-form]');
    if (filterForm) {
      filterForm.addEventListener('change', (e) => {
        if (e.target.matches('.dp-filter-option__input')) filterForm.requestSubmit();
      });

      let priceTimer = null;
      filterForm.querySelectorAll('[data-dp-filter-price]').forEach((input) => {
        input.addEventListener('change', () => {
          clearTimeout(priceTimer);
          priceTimer = setTimeout(() => filterForm.requestSubmit(), 250);
        });
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            clearTimeout(priceTimer);
            filterForm.requestSubmit();
          }
        });
      });

      // Drop empty price params so URLs stay clean.
      filterForm.addEventListener('submit', () => {
        filterForm.querySelectorAll('[data-dp-filter-price]').forEach((input) => {
          if (input.value === '') input.disabled = true;
        });
      });
    }

    // Sort → navigate preserving active filters. Uses the current URL
    // querystring (which already has all filter params) and swaps
    // sort_by, so navigating doesn't reset filters.
    const sort = document.querySelector('[data-dp-sort-select]');
    if (sort) {
      sort.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        window.location.href = url.toString();
      });
    }
  });
})();

/* Collection body-wrap removed — filter sidebar now uses the clean
   theme's proven `collection-filters-sidebar` / `filters` snippets
   which render with Pipeline's existing sidebar CSS + accordion JS. */

(function () {
  window.DP.register('cart-items', function () {
    const root = document.querySelector('[data-dp-cart-items]');
    if (!root) return;

    function lineKey(el) { return el.closest('[data-dp-line-key]')?.dataset.dpLineKey; }
    function setItemLoading(busy) {
      root.querySelectorAll('[data-dp-line-key]').forEach(item => item.classList.toggle('is-loading', busy));
    }
    async function reload() {
      const cart = await window.DP.cart.get();
      document.dispatchEvent(new CustomEvent('dp:cart:updated', { detail: cart, bubbles: true }));
      window.location.reload();
    }

    window.DP.bindAction('qty-increment', async (el) => {
      setItemLoading(true);
      const input = el.parentElement.querySelector('[data-dp-qty-input]');
      const qty = parseInt(input.value, 10) + 1;
      await window.DP.cart.change(lineKey(el), qty);
      await reload();
    });
    window.DP.bindAction('qty-decrement', async (el) => {
      setItemLoading(true);
      const input = el.parentElement.querySelector('[data-dp-qty-input]');
      const qty = Math.max(0, parseInt(input.value, 10) - 1);
      await window.DP.cart.change(lineKey(el), qty);
      await reload();
    });
    window.DP.bindAction('remove-line', async (el) => {
      setItemLoading(true);
      await window.DP.cart.change(lineKey(el), 0);
      await reload();
    });

    window.DP.bindQtyInputs(root, '[data-dp-qty-input]', async function (input, qty) {
      setItemLoading(true);
      await window.DP.cart.change(lineKey(input), qty);
      await reload();
    });

    const notes = document.querySelector('[data-dp-cart-notes]');
    if (notes) {
      const save = window.DP.debounce(() => {
        window.DP.cart.update({ note: notes.value });
      }, 600);
      notes.addEventListener('input', save);
    }
  });

  /* Cart discount form. /cart/update.js `discount` replaces every code on
     the cart, so merge the new code with the ones already applied. */
  window.DP.register('cart-discount', function () {
    document.querySelectorAll('[data-dp-discount-form]').forEach(function (form) {
      const input = form.querySelector('input[name="discount"]');
      const button = form.querySelector('[type="submit"]');
      const error = form.querySelector('[data-dp-discount-error]');
      function showError(msg) {
        if (!error) return;
        error.textContent = msg;
        error.hidden = !msg;
      }

      form.addEventListener('submit', async function (e) {
        e.preventDefault();
        const code = input.value.trim();
        if (!code) return;
        showError('');
        button.disabled = true;
        try {
          const current = await window.DP.cart.get(true);
          const codes = (current.discount_codes || [])
            .filter(d => d.applicable)
            .map(d => d.code)
            .filter(c => c.toLowerCase() !== code.toLowerCase());
          codes.push(code);
          const cart = await window.DP.cart.update({ discount: codes.join(',') });
          const applied = (cart.discount_codes || [])
            .find(d => d.code.toLowerCase() === code.toLowerCase());
          if (applied && applied.applicable) {
            window.location.reload();
            return;
          }
          showError('"' + code + '" isn’t valid for the items in your cart.');
        } catch (err) {
          console.warn('[dp] discount apply failed:', err);
          showError('Couldn’t apply discount. Please try again.');
        }
        button.disabled = false;
      });
    });

    window.DP.bindAction('remove-discount', async function (el) {
      const code = el.dataset.code.toLowerCase();
      el.disabled = true;
      try {
        const current = await window.DP.cart.get(true);
        const codes = (current.discount_codes || [])
          .filter(d => d.applicable && d.code.toLowerCase() !== code)
          .map(d => d.code);
        await window.DP.cart.update({ discount: codes.join(',') });
        window.location.reload();
      } catch (err) {
        console.warn('[dp] discount remove failed:', err);
        el.disabled = false;
      }
    });
  });
})();

/* ============================================================
   dp-shipping-estimator — informational only.
   User enters country / state / ZIP → we fetch Shopify rates and render
   TWO SEPARATE ROWS: "Estimated shipping" + "Estimated customs".
   The actual prepay-vs-handle-yourself choice is made at CHECKOUT only;
   this estimator never asks the customer to pick.
   ============================================================ */
(function () {
  window.DP.register('shipping-estimator', function () {
    const root = document.querySelector('[data-dp-shipping]');
    if (!root) return;

    const state = {
      country: null,
      province: '',
      postal: '',
      rates: [],
      labels: null,
    };

    const threshold      = parseInt(root.dataset.freeShippingThreshold, 10) || 0;
    const defaultCountry = root.dataset.defaultCountry || 'US';

    // ---------- Country + Province selects ----------
    // Both fields are <select>. The country select is populated by
    // Shopify's `all_country_option_tags`, whose <option> elements carry
    // `data-provinces='[["Alabama","AL"],...]'`. We read that to populate
    // the province select. If a country has no provinces, we hide the
    // province field entirely.
    const countrySelect  = root.querySelector('[data-dp-country]');
    const provinceSelect = root.querySelector('[data-dp-province]');
    const provinceWrap   = root.querySelector('[data-dp-province-wrap]');
    const postalInput    = root.querySelector('[data-dp-postal]');

    function storedCountry() {
      return localStorage.getItem('dp_shipping_country')
        || (window.Shopify && window.Shopify.country)
        || defaultCountry;
    }

    function populateProvinces(selectedCountryCode) {
      if (!countrySelect || !provinceSelect) return;
      const opt = countrySelect.options[countrySelect.selectedIndex];
      const raw = (opt && opt.getAttribute('data-provinces')) || '[]';
      let list = [];
      try { list = JSON.parse(raw); } catch (_) { list = []; }
      provinceSelect.innerHTML = '';
      if (!list.length) {
        if (provinceWrap) provinceWrap.hidden = true;
        state.province = '';
        return;
      }
      if (provinceWrap) provinceWrap.hidden = false;
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select state / province';
      provinceSelect.appendChild(placeholder);
      list.forEach(function (pair) {
        const o = document.createElement('option');
        o.value = pair[1] || pair[0];
        o.textContent = pair[0];
        provinceSelect.appendChild(o);
      });
      const saved = localStorage.getItem('dp_shipping_province') || '';
      if (saved) {
        provinceSelect.value = saved;
        state.province = provinceSelect.value; // empty if saved doesn't match
      }
    }

    if (countrySelect) {
      // Shopify's all_country_option_tags pre-selects based on shop locale —
      // override with our stored preference so the customer's last choice wins.
      const initial = storedCountry();
      for (let i = 0; i < countrySelect.options.length; i++) {
        if (countrySelect.options[i].value === initial) { countrySelect.selectedIndex = i; break; }
      }
      state.country = countrySelect.value;
      populateProvinces(state.country);
      countrySelect.addEventListener('change', function () {
        state.country = countrySelect.value;
        localStorage.setItem('dp_shipping_country', state.country);
        localStorage.removeItem('dp_shipping_province');
        populateProvinces(state.country);
        state.province = provinceSelect ? provinceSelect.value : '';
        renderVariant();
      });
    }

    if (provinceSelect) {
      provinceSelect.addEventListener('change', function () {
        state.province = provinceSelect.value;
      });
    }

    if (postalInput) {
      state.postal = localStorage.getItem('dp_shipping_zip') || '';
      postalInput.value = state.postal;
      postalInput.addEventListener('input', () => { state.postal = postalInput.value.trim(); });
    }

    // Legacy hook kept so any callers still wired to setCountry() don't break.
    function setCountry(code) {
      if (!countrySelect) return;
      for (let i = 0; i < countrySelect.options.length; i++) {
        if (countrySelect.options[i].value === code) { countrySelect.selectedIndex = i; break; }
      }
      state.country = countrySelect.value;
      localStorage.setItem('dp_shipping_country', state.country);
      populateProvinces(state.country);
      renderVariant();
    }

    // ---------- Variant visibility ----------
    // "us" panel is just the free-shipping progress bar; the results panel
    // below is shared across domestic + international.
    const usVariant = root.querySelector('[data-dp-variant="us"]');
    function renderVariant() {
      const isUS = state.country === 'US';
      if (usVariant) usVariant.hidden = !isUS;
      if (isUS) renderProgress();
    }

    // ---------- US: free shipping progress ----------
    function renderProgress() {
      const fill  = usVariant.querySelector('[data-dp-progress-fill]');
      const label = usVariant.querySelector('[data-dp-progress-label]');
      window.DP.cart.get().then((cart) => {
        const total = cart.total_price;
        const pct   = Math.min(100, Math.round((total / threshold) * 100));
        if (fill)  fill.style.width = pct + '%';
        if (label) {
          if (total >= threshold) {
            label.textContent = 'Free shipping unlocked \u2713';
          } else {
            const rem = window.DP.formatMoney(threshold - total);
            label.textContent = rem + ' away from free shipping';
          }
        }
      });
    }

    // ---------- Rate fetch ----------
    // Using Shopify's sync endpoint `/cart/shipping_rates.json` — same one
    // Pipeline uses (verified in keebio-current-original/assets/theme.js).
    // Simpler than the async `prepare_shipping_rates` + polling pattern,
    // and avoids timing out on slow carrier responses. Shopify will block
    // the request up to ~10s if carrier rates take time, then respond.
    async function fetchRates() {
      if (!state.country || !state.postal) return;
      localStorage.setItem('dp_shipping_zip', state.postal);
      if (state.province) localStorage.setItem('dp_shipping_province', state.province);
      if (stateEl) stateEl.innerHTML = '';
      if (resultsEl) resultsEl.hidden = true;
      setLoading(true);
      try {
        const params = new URLSearchParams({
          'shipping_address[country]':  state.country,
          'shipping_address[province]': state.province || '',
          'shipping_address[zip]':      state.postal,
        }).toString();
        const res = await fetch('/cart/shipping_rates.json?' + params, {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' },
        });
        if (!res.ok) throw new Error('rates-http-' + res.status);
        const data = await res.json();
        const rates = data.shipping_rates || [];
        state.rates = rates;
        if (rates.length === 0 || (rates.length === 1 && rates[0].name === 'Shipping Unavailable for Order')) {
          renderNoRates();
        } else {
          renderResults(rates);
        }
      } catch (err) {
        console.warn('[dp-shipping-estimator] fetch failed:', err);
        renderError();
      } finally {
        setLoading(false);
      }
    }
    window.DP.bindAction('estimate-shipping', fetchRates);

    // ---------- Pick the representative rate ----------
    // Pick the cheapest rate in a reasonable delivery window. We only need
    // one rate to show "estimated shipping" — the customer doesn't pick.
    const DUTY_REGEX = /\(Pre-Paid Duties of \$?([\d.]+)(?:\s*USD)?\s*Included\)/i;

    // Strip the "(Pre-Paid Duties of X USD Included)" suffix so the
    // displayed carrier name stays clean. Customs get shown separately.
    function cleanRateName(name) {
      return (name || '')
        .replace(DUTY_REGEX, '')
        .replace(/\s*DDU$/i, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
    }

    // Pick up to 3 tiered rates: cheapest, median, and the one with
    // fastest delivery that isn't the cheapest. If there are 1–3 rates,
    // show them all. If there are more, sample to 3.
    function pickTiered(rates) {
      if (!rates.length) return [];
      const sorted = rates.slice().sort(function(a, b) { return parseFloat(a.price) - parseFloat(b.price); });
      if (sorted.length <= 3) return sorted;
      // Cheapest, roughly middle, most expensive — typically corresponds
      // to Economy / Standard / Express for the same carrier.
      return [
        sorted[0],
        sorted[Math.floor(sorted.length / 2)],
        sorted[sorted.length - 1],
      ];
    }

    // Extract the pre-paid duty amount from any rate that carries the
    // "(Pre-Paid Duties of X USD Included)" suffix. Shopify emits the
    // same duty amount on all DDP rates for the same destination, so
    // reading from any matching rate is accurate.
    function extractDutyCents(rates) {
      for (const r of rates) {
        const m = (r.name || '').match(DUTY_REGEX);
        if (m) return Math.round(parseFloat(m[1]) * 100);
      }
      return 0;
    }

    // ---------- Render results ----------
    const resultsEl     = root.querySelector('[data-dp-results]');
    const ratesListEl   = root.querySelector('[data-dp-rates-list]');
    const breakdownNote = root.querySelector('[data-dp-breakdown-note]');
    const freeBanner    = root.querySelector('[data-dp-free-banner]');
    const freeBannerTxt = root.querySelector('[data-dp-free-banner-text]');
    const customsRow    = root.querySelector('[data-dp-row-customs]');
    const customsValue  = root.querySelector('[data-dp-customs-value]');
    const customsSub    = root.querySelector('[data-dp-customs-sub]');

    // Show/hide the "qualifies for free shipping" banner based on US cart
    // total vs the configured threshold. Separate from the rate list so
    // the shipping numbers below still read as carrier truth.
    function updateFreeShippingBanner() {
      if (!freeBanner) return;
      const isUS = state.country === 'US' || state.country === 'United States';
      if (!isUS || threshold <= 0) { freeBanner.hidden = true; return; }
      window.DP.cart.get().then(function(cart) {
        if (cart && cart.total_price >= threshold) {
          freeBanner.hidden = false;
          if (freeBannerTxt) {
            freeBannerTxt.innerHTML = 'Your order qualifies for <strong>free US shipping</strong>, applied at checkout. Carrier rates below are still shown for reference.';
          }
        } else {
          freeBanner.hidden = true;
        }
      }).catch(function() { freeBanner.hidden = true; });
    }

    function renderResults(rates) {
      if (!resultsEl || !ratesListEl) return;
      if (stateEl) stateEl.innerHTML = '';
      ratesListEl.innerHTML = '';
      if (breakdownNote) breakdownNote.textContent = '';
      if (customsRow) customsRow.hidden = true;
      if (!rates.length) {
        resultsEl.hidden = false;
        ratesListEl.innerHTML = '<div class="dp-shipping-rate dp-shipping-rate--empty">No rates available for this destination. Shipping will be calculated at checkout.</div>';
        return;
      }
      resultsEl.hidden = false;

      const tiered = pickTiered(rates);
      const dutyCents = extractDutyCents(tiered.length ? tiered : rates);

      // When all tiered rates include DDP, show the NET shipping
      // (rate price minus customs) so customers aren't scared by the
      // total-with-duties number. Customs gets its own row below.
      // If only some rates are DDP, keep prices as-is to avoid mixing
      // apples and oranges — the footer note still explains the situation.
      const allDDP = tiered.length > 0 && tiered.every(function(r) {
        return DUTY_REGEX.test(r.name || '');
      });
      const splitOutDuty = allDDP && dutyCents > 0;
      const hasDDU = tiered.some(function(r) { return /DDU$/i.test((r.name || '').trim()); });

      tiered.forEach(function(rate, idx) {
        const cheapest = idx === 0;
        const el = document.createElement('div');
        el.className = 'dp-shipping-rate' + (cheapest ? ' dp-shipping-rate--cheapest' : '');

        // If we're splitting out duty, the displayed price is the
        // shipping-only portion. At checkout the rate's DDP total is
        // back in one line; customs becomes a line item at checkout,
        // so the breakdown stays consistent across the funnel.
        const fullPriceCents = Math.round(parseFloat(rate.price) * 100);
        const shownCents     = splitOutDuty ? Math.max(0, fullPriceCents - dutyCents) : fullPriceCents;

        el.innerHTML = ''
          + '<div class="dp-shipping-rate__info">'
          +   '<div class="dp-shipping-rate__name">' + escapeHtml(cleanRateName(rate.name)) + '</div>'
          +   '<div class="dp-shipping-rate__eta">'  + escapeHtml(formatETA(rate)) + '</div>'
          + '</div>'
          + '<div class="dp-shipping-rate__price">' + window.DP.formatMoney(shownCents) + '</div>';
        ratesListEl.appendChild(el);
      });

      // Customs row — separate additive line. Only shown when we've
      // actually subtracted the duty from the displayed shipping prices.
      const isUS = state.country === 'US' || state.country === 'United States';
      if (customsRow) {
        if (splitOutDuty && !isUS) {
          customsRow.hidden = false;
          if (customsValue) customsValue.textContent = window.DP.formatMoney(dutyCents);
          if (customsSub)   customsSub.textContent   = 'Prepaid at checkout for convenience.';
        } else {
          customsRow.hidden = true;
        }
      }

      // Footer note — context matching the destination.
      if (breakdownNote) {
        if (isUS) {
          breakdownNote.textContent = 'The final rate is locked at checkout.';
        } else if (splitOutDuty) {
          breakdownNote.innerHTML =
            'Rates above are carrier shipping only. Customs &amp; duties rates shown here are prepaid to the carrier at checkout '
            + '(so nothing extra is charged at delivery). '
            + 'Final rates are locked at checkout.';
        } else if (hasDDU) {
          breakdownNote.textContent = 'These rates do not include customs &amp; duties, which may be due upon delivery. The final rate is locked at checkout.';
        } else if (dutyCents > 0) {
          const dutyStr = window.DP.formatMoney(dutyCents);
          breakdownNote.innerHTML =
            'Each rate is all-in and includes <strong>' + escapeHtml(dutyStr) + '</strong> in customs & duties prepaid to the carrier. The final rate is locked at checkout.';
        } else {
          breakdownNote.textContent = 'These rates are estimates. Customs & duties (if any) will be shown at checkout. The final rate is locked at checkout.';
        }
      }

      updateFreeShippingBanner();

      window.DP.cart.update({
        attributes: {
          dp_shipping_country:  state.country,
          dp_shipping_province: state.province,
        }
      });
    }

    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function(c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
    function estimateDays(rate) {
      if (rate.delivery_range && rate.delivery_range.length === 2) {
        const days = Math.round((new Date(rate.delivery_range[1]) - new Date()) / 86400000);
        return Math.max(1, days);
      }
      const name = rate.name.toLowerCase();
      if (name.indexOf('overnight') !== -1)                                  return 1;
      if (name.indexOf('express') !== -1 || name.indexOf('priority') !== -1) return 3;
      if (name.indexOf('economy') !== -1 || name.indexOf('ground') !== -1)   return 6;
      return 5;
    }
    function formatETA(rate) {
      if (rate.delivery_range && rate.delivery_range.length === 2) {
        const fmt = { month: 'short', day: 'numeric' };
        return 'Arrives ' + new Date(rate.delivery_range[0]).toLocaleDateString('en-US', fmt)
          + '\u2013' + new Date(rate.delivery_range[1]).toLocaleDateString('en-US', fmt);
      }
      return '~' + estimateDays(rate) + ' business days';
    }

    // ---------- Loading + error ----------
    const stateEl = root.querySelector('[data-dp-state]');
    const calcBtn = root.querySelector('[data-dp-action="estimate-shipping"]');
    const calcBtnLabel = calcBtn ? calcBtn.textContent : 'Calculate';
    function setLoading(on) {
      if (stateEl) stateEl.classList.toggle('is-loading', on);
      if (calcBtn) {
        calcBtn.disabled = on;
        calcBtn.textContent = on ? 'Calculating…' : calcBtnLabel;
      }
    }
    function renderError() {
      if (resultsEl) resultsEl.hidden = true;
      if (stateEl) stateEl.innerHTML = '<div class="dp-shipping-estimator__error">Couldn\'t fetch rates — shipping calculated at checkout. Try checking postal code or retry later.</div>';
    }
    function renderNoRates() {
      // Distinct from renderError: the API responded successfully but with
      // zero rates — typically because Keebio hasn't configured a shipping
      // zone for the entered country/zip. Tell the customer honestly.
      if (stateEl) stateEl.innerHTML = '<div class="dp-shipping-estimator__error">We can\'t calculate shipping to this location automatically. Try a different postal code, or proceed to checkout and we\'ll confirm availability there.</div>';
      // Also hide the results panel so the "—" placeholders don't linger.
      const results = root.querySelector('[data-dp-results]');
      if (results) results.hidden = true;
    }
    // ---------- Country picker ----------
    const COUNTRIES = [
      { code: 'US', name: 'United States', flag: '\uD83C\uDDFA\uD83C\uDDF8' },
      { code: 'CA', name: 'Canada',         flag: '\uD83C\uDDE8\uD83C\uDDE6' },
      { code: 'GB', name: 'United Kingdom', flag: '\uD83C\uDDEC\uD83C\uDDE7' },
      { code: 'DE', name: 'Germany',        flag: '\uD83C\uDDE9\uD83C\uDDEA' },
      { code: 'FR', name: 'France',         flag: '\uD83C\uDDEB\uD83C\uDDF7' },
      { code: 'AU', name: 'Australia',      flag: '\uD83C\uDDE6\uD83C\uDDFA' },
      { code: 'JP', name: 'Japan',          flag: '\uD83C\uDDEF\uD83C\uDDF5' },
    ];
    window.DP.bindAction('toggle-country-picker', function() {
      const list = root.querySelector('[data-dp-country-list]');
      if (!list) return;
      if (list.hidden && list.childElementCount === 0) {
        COUNTRIES.forEach(function(c) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'dp-country-option';
          btn.textContent = c.flag + ' ' + c.name;
          btn.addEventListener('click', function() {
            setCountry(c.code, c.flag, c.name);
            list.hidden = true;
          });
          list.appendChild(btn);
        });
      }
      list.hidden = !list.hidden;
    });

    // ---------- Boot ----------
    // Don't auto-fetch on load — user explicitly clicks "Calculate".
    // Country picker pre-selects their detected country; fields populate
    // from localStorage so a return visit keeps their last inputs.
    // Resolution order: stored choice > Liquid-provided default (which
    // reads localization.country.iso_code) > 'US' fallback.
    state.country = (function () {
      try {
        var stored = localStorage.getItem('dp_shipping_country');
        if (stored) return stored;
      } catch (_) {}
      return (window.Shopify && window.Shopify.country) || defaultCountry || 'US';
    })();
    const initial = COUNTRIES.find(function(c) { return c.code === state.country; });
    if (initial) {
      root.querySelector('[data-dp-country-flag]').textContent = initial.flag;
      root.querySelector('[data-dp-country-name]').textContent = initial.name;
    }
    renderVariant();
    // If the user previously calculated, re-run once so they don't have to.
    if (state.country && state.postal) fetchRates();
  });
})();
/* ============================================================
   dp-tabbed-options module
   Handles tab-button → panel switching for dp-tabbed-options sections.
   ============================================================ */
(function () {
  window.DP.register('tabbed-options', function () {
    document.querySelectorAll('[data-dp-tabbed-options]').forEach(function (root) {
      var buttons = root.querySelectorAll('[data-dp-tab-button]');
      var panels  = root.querySelectorAll('[data-dp-tab-panel]');

      function activate(id) {
        buttons.forEach(function (btn) {
          var isMatch = btn.getAttribute('data-tab-id') === id;
          btn.classList.toggle('is-active', isMatch);
          btn.setAttribute('aria-selected', isMatch ? 'true' : 'false');
        });
        panels.forEach(function (panel) {
          var isMatch = panel.getAttribute('data-dp-tab-panel') === id;
          panel.classList.toggle('is-active', isMatch);
          if (isMatch) {
            panel.removeAttribute('hidden');
          } else {
            panel.setAttribute('hidden', '');
          }
        });
      }

      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          activate(btn.getAttribute('data-tab-id'));
        });
        btn.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activate(btn.getAttribute('data-tab-id'));
          }
        });
      });

      // Init: activate default or first tab
      var defaultId = root.getAttribute('data-default-tab');
      var firstBtn  = buttons[0];
      if (defaultId) {
        activate(defaultId);
      } else if (firstBtn) {
        activate(firstBtn.getAttribute('data-tab-id'));
      }
    });
  });
})();

/* ============================================================
   dp-discount-copy module
   Clipboard copy for dp-discount-banner sections.
   ============================================================ */
(function () {
  window.DP.register('discount-copy', function () {
    window.DP.bindAction('copy-discount', function (el) {
      var code = el.getAttribute('data-code') || '';
      if (!code) return;

      function markCopied() {
        el.classList.add('is-copied');
        setTimeout(function () {
          el.classList.remove('is-copied');
        }, 1500);
      }

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(markCopied).catch(fallback);
      } else {
        fallback();
      }

      function fallback() {
        try {
          var ta = document.createElement('textarea');
          ta.value = code;
          ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          markCopied();
        } catch (err) {
          console.warn('[dp] discount copy fallback failed:', err);
        }
      }
    });
  });
})();
/* bump 1776813045 */

/* cachebust 1776823725 */

/* ============================================================
   dp-cart-cs — quick-add for the cart page cross-sell cards.
   Submits via fetch so the cart reloads in place, and (if the
   product happens to have configurator options) the DP app's
   patched fetch gets a chance to process it.
   ============================================================ */
(function () {
  window.DP.register('cart-cross-sells-add', function () {
    document.querySelectorAll('form[data-dp-cart-cs]').forEach(function (form) {
      if (form.__dpCsBound) return;
      form.__dpCsBound = true;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('button[type="submit"]');
        if (btn) { btn.disabled = true; btn.dataset.dpLabel = btn.textContent; btn.textContent = 'Adding...'; }
        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Accept': 'application/javascript' },
          body: new FormData(form)
        })
        .then(function (r) { return r.ok ? r.json() : r.json().then(function (err) { return Promise.reject(err); }); })
        .then(function () { window.location.reload(); })
        .catch(function (err) {
          console.error('[dp-cart-cs] add failed:', err);
          alert((err && (err.description || err.message)) || 'Could not add to cart.');
          if (btn) { btn.disabled = false; btn.textContent = btn.dataset.dpLabel || 'Add'; }
        });
      });
    });
  });
})();

/* ============================================================
   dp-cart-layout — wrap left-column sections in a single flex
   container so the CSS grid only has two cells (left stack +
   sticky summary). Without this wrapper, summary spans multiple
   grid rows and forces each left section into a row as tall as
   summary_height / N, creating 100–150px voids after each
   section's content.
   ============================================================ */
(function () {
  if (!document.body.classList.contains('template-cart')) return;
  var main = document.getElementById('MainContent') || document.querySelector('.template-cart main');
  if (!main) return;
  if (main.querySelector('.dp-cart-left')) return;

  var leftSelectors = [
    '.dp-cart-items-section',
    '.dp-cart-cross-sells-section',
    '.dp-cart-notes-section',
    '.dp-cart-trust-section'
  ];
  var first = main.querySelector(leftSelectors.join(','));
  if (!first) return;

  var wrap = document.createElement('div');
  wrap.className = 'dp-cart-left';
  first.parentNode.insertBefore(wrap, first);

  leftSelectors.forEach(function (sel) {
    main.querySelectorAll(sel).forEach(function (el) { wrap.appendChild(el); });
  });
})();

/* ============================================================
   dp-atc-label — normalize every Add-to-cart button label to the
   "LABEL • $PRICE" format. The Divine Power product-options-app (and
   Shopify's own variant scripts) write the running total directly
   into `.btn__text` using different separators ("Add to cart $X",
   "Add to cart — $X", "Add to cart – $X"). We watch that node with
   a MutationObserver and rewrite the separator to " • " so the
   button always renders consistently.
   ============================================================ */
(function () {
  function format(el) {
    if (!el || el.__dpAtcFormatting) return;
    var t = (el.textContent || '').trim();
    if (!t) return;
    // Split at first currency-prefixed price token. The DP options app
    // often concatenates without a space ("Add to cart$320.00"), so we
    // allow zero-or-more whitespace AND zero-or-more separator chars.
    var m = t.match(/^(.+?)\s*[-–—·•]?\s*(\$[0-9][0-9.,]*.*)$/);
    if (!m) return;
    var label = m[1].trim();
    var price = m[2].trim();
    var desired = label + ' • ' + price;
    if (t === desired) return;
    el.__dpAtcFormatting = true;
    el.textContent = desired;
    el.__dpAtcFormatting = false;
  }
  function wire(el) {
    if (el.__dpAtcWired) return;
    el.__dpAtcWired = true;
    format(el);
    var obs = new MutationObserver(function () { format(el); });
    obs.observe(el, { childList: true, characterData: true, subtree: true });
  }
  function scan() {
    document.querySelectorAll('.btn--add-to-cart .btn__text, .dp-product__atc .btn__text, .dp-sticky-atc button .btn__text').forEach(wire);
    // Fallback: buttons without a .btn__text span — wire the button itself.
    document.querySelectorAll('.btn--add-to-cart, .dp-product__atc, .dp-sticky-atc button[type="submit"]').forEach(function (btn) {
      if (!btn.querySelector('.btn__text')) wire(btn);
    });
  }
  if (document.readyState !== 'loading') scan();
  else document.addEventListener('DOMContentLoaded', scan);
  // Re-scan after cart/variant changes in case new nodes appeared.
  ['cart:updated', 'theme:cart:change', 'dp:cart:updated', 'variant:change'].forEach(function (ev) {
    document.addEventListener(ev, scan);
  });
})();

/* ============================================================
   dp-product-atc — intercept native ATC form submit, re-send via
   fetch so the Divine Power product-options-app's patched fetch
   gets a chance to transform `configurator[X]=...` fields into
   a multi-item cart add (base + bundle children). The app also
   dispatches `cart:updated` and `theme:cart:change` on document
   after a successful bundle add — re-broadcast those as our own
   `dp:cart:updated` so the mini-cart / drawer refresh in place.
   ============================================================ */
document.addEventListener('cart:updated', function (e) {
  var cart = (e.detail && e.detail.cart) || e.detail;
  if (cart) {
    document.dispatchEvent(new CustomEvent('dp:cart:updated', { detail: cart, bubbles: true }));
  } else if (window.DP && window.DP.cart) {
    window.DP.cart.get().then(function (c) {
      document.dispatchEvent(new CustomEvent('dp:cart:updated', { detail: c, bubbles: true }));
    });
  }
});
document.addEventListener('theme:cart:change', function (e) {
  var cart = (e.detail && e.detail.cart) || e.detail;
  if (cart) {
    document.dispatchEvent(new CustomEvent('dp:cart:updated', { detail: cart, bubbles: true }));
  }
});

(function () {
  window.DP.register('product-atc', function () {
    document.querySelectorAll('form[data-dp-atc-form]').forEach(function (form) {
      if (form.__dpBound) return;
      form.__dpBound = true;

      form.addEventListener('submit', function (e) {
        // Don't intercept if the button is already in a submitting state,
        // or if a modifier key is held (allow power users to fall through).
        if (e.defaultPrevented) return;
        e.preventDefault();

        var submitBtn = form.querySelector('button[type="submit"], .dp-product__atc');
        var originalLabel = submitBtn && submitBtn.textContent;
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.dataset.dpOriginalLabel = originalLabel;
          submitBtn.textContent = 'Adding...';
        }

        var formData = new FormData(form);

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Accept': 'application/javascript' },
          body: formData
        })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (err) { return Promise.reject(err); });
          return r.json();
        })
        .then(function (item) {
          // Refresh cart badges and any subscribed modules.
          return window.DP.cart.get().then(function (cart) {
            document.dispatchEvent(new CustomEvent('dp:cart:updated', { detail: cart, bubbles: true }));
            return cart;
          });
        })
        .then(function () {
          // Open the cart drawer if present; otherwise navigate to /cart.
          var drawer = document.querySelector('[data-dp-cart-drawer], [data-dp-mini-cart]');
          if (drawer && window.DP.actions && window.DP.actions['toggle-mini-cart']) {
            window.DP.actions['toggle-mini-cart'](drawer);
          } else if (drawer && window.DP.actions && window.DP.actions['open-cart-drawer']) {
            window.DP.actions['open-cart-drawer'](drawer);
          } else {
            window.location.href = '/cart';
          }
        })
        .catch(function (err) {
          console.error('[dp-product-atc] add failed:', err);
          var msg = (err && (err.description || err.message)) || 'Could not add to cart.';
          alert(msg);
        })
        .finally(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = submitBtn.dataset.dpOriginalLabel || 'Add to cart';
          }
        });
      });
    });
  });
})();

/* ============================================================
   dp-payment-interceptor — make Shop Pay / Apple Pay / GPay
   configurator-aware via cart-then-redirect.
   ------------------------------------------------------------
   Background:
     Shopify's `payment_button` Liquid filter renders accelerated
     checkout buttons (Shop Pay, Apple Pay, GPay). Those buttons
     read only the form's variant `id` and build their own
     checkout request — they bypass /cart/add.js entirely AND
     ignore extra form fields like `shopify-additional-items[i]`.

     The Divine Power product-options-app patches `window.fetch`
     to intercept POST /cart/add and expand `configurator[*]`
     selections into a multi-line bundle (parent + children with
     `_bundle_id` / `_is_bundle_parent` / `_bundle_child` line
     item properties). This is the ONLY path that produces a
     correctly-priced bundle in the cart, because the cart
     transformer extension reads those properties and merges the
     lines at checkout while preserving each line's price.

     For configurator products, we therefore can't let Shop Pay
     submit directly — it would skip the bundle expansion and
     undercharge the customer (only the parent variant lands in
     the cart). Instead we:
       1. Capture-phase intercept the click on `[data-dp-express-form]`
          BEFORE Shop Pay's own listener fires.
       2. POST the section's ATC form (which the DP options app
          observes) to /cart/add.js — the patched fetch fans
          out the bundle children with correct prices/properties.
       3. Set aria-busy on the express container so CSS shows
          the loading overlay (.dp-product__express-loading).
       4. Redirect to /checkout once the cart-add resolves. The
          buyer lands on Shopify's standard checkout page where
          Shop Pay (and Apple Pay / GPay) is still selectable as
          the accelerated payment option — just not 1-click sheet.

   For SIMPLE products (no `configurator[*]` inputs in the section)
   the listener is a no-op — Shop Pay's 1-click sheet works exactly
   as before.

   Spec context (why we don't use shopify-additional-items):
     https://shopify.dev/docs/storefronts/themes/pricing-payments/accelerated-checkout/multiple-product-checkout
   That pattern only applies to the regular `/cart/add` form post.
   Shop Pay's `<shop-pay-button>` web component reads its `variants`
   attribute, not arbitrary form fields, so additional-items inputs
   are silently dropped — empirically verified: cart receives only
   the parent variant, line item properties survive but no children.
   ============================================================ */
(function () {
  window.DP.register('payment-interceptor', function () {
    document.querySelectorAll('form[data-dp-express-form]').forEach(function (expressForm) {
      if (expressForm.__dpPmtIntBound) return;
      expressForm.__dpPmtIntBound = true;

      expressForm.addEventListener('click', function (e) {
        // Only respond to plain primary-button clicks. Modifier keys
        // (cmd/ctrl/shift/alt) and non-primary buttons indicate the
        // user is doing something special (e.g. cmd-click → new tab).
        if (e.button !== 0 && typeof e.button === 'number') return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

        // Locate the section's ATC form — that's where the DP options
        // app injects `configurator[*]` fields, and where its patched
        // fetch reads selection state from.
        var section = expressForm.closest('.dp-product-section, .dp-product-section-simple, section');
        if (!section) return;
        var atcForm = section.querySelector('form[data-dp-atc-form]');
        if (!atcForm) return; // No ATC form = nothing to bundle, let click pass.

        // Detect configurator presence by scanning the section's inputs.
        // Match the same selectors the DP options app uses internally.
        var hasConfigurator = !!section.querySelector(
          'input[type="radio"][name^="configurator["], ' +
          'select[data-is-dropdown="1"][name^="configurator["], ' +
          'input[data-is-text-input="1"][name^="configurator["]'
        );
        if (!hasConfigurator) return; // Simple product: let Shop Pay through.

        // ---- Configurator product: preempt Shop Pay's own click handler ----
        e.preventDefault();
        e.stopImmediatePropagation();

        // Find the loading overlay container (`.dp-product__express`),
        // mark it busy. CSS handles the visual: dim payment buttons,
        // show spinner + "Preparing checkout…" message centered.
        var expressContainer = expressForm.closest('.dp-product__express') || expressForm;
        expressContainer.setAttribute('aria-busy', 'true');
        // Also block pointer events so the user can't repeat-click
        // through the dimmed UI during the round-trip.
        expressForm.style.pointerEvents = 'none';

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Accept': 'application/javascript' },
          body: new FormData(atcForm)
        })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (err) { return Promise.reject(err); });
          return r.json();
        })
        .then(function () {
          // Hand off to Shopify's checkout. Cart now contains the full
          // bundle (parent + children with shared _bundle_id), so the
          // cart transformer will merge them on display while preserving
          // the SUM of prices. Shop Pay / Apple Pay / GPay are still
          // available on the checkout page as accelerated options.
          window.location.href = '/checkout';
        })
        .catch(function (err) {
          console.error('[dp-payment-interceptor] add failed:', err);
          expressContainer.removeAttribute('aria-busy');
          expressForm.style.pointerEvents = '';
          var msg = (err && (err.description || err.message)) || 'Could not start checkout. Please try Add to cart instead.';
          alert(msg);
        });
      }, true); // CAPTURE phase — fires before payment_button's own listeners
    });
  });
})();

/* ============================================================
   dp-email-capture — Klaviyo-vs-native fallback swap
   ------------------------------------------------------------
   The section renders both a Klaviyo embed target div AND a native
   Shopify form. Native is visible by default (.dp-email-capture__form-
   stack has no modifier class). When Klaviyo's script populates the
   embed div with actual form content, we flip the stack into
   "klaviyo-loaded" mode so the native fallback hides.

   Detection: a MutationObserver on the Klaviyo target div fires the
   moment Klaviyo injects anything. If Klaviyo never loads (form not
   published, script blocked, form type is popup not embed, etc.),
   the native form stays visible — section is never empty.
   ============================================================ */
(function () {
  window.DP.register('email-capture', function () {
    document.querySelectorAll('[data-dp-email-capture]').forEach(function(stack) {
      const formId = stack.dataset.klaviyoFormId;
      if (!formId) return; // No Klaviyo configured — native form is the only render
      const klaviyoDiv = stack.querySelector('[data-dp-klaviyo-target]');
      if (!klaviyoDiv) return;

      let resolved = false;
      function markLoaded() {
        if (resolved) return;
        resolved = true;
        stack.classList.add('dp-email-capture--klaviyo-loaded');
      }

      // If Klaviyo already injected before our JS ran, catch it immediately.
      if (klaviyoDiv.children.length > 0 || klaviyoDiv.innerHTML.trim() !== '') {
        markLoaded();
        return;
      }

      // Watch for Klaviyo's injection.
      const obs = new MutationObserver(function() {
        if (klaviyoDiv.children.length > 0) {
          markLoaded();
          obs.disconnect();
        }
      });
      obs.observe(klaviyoDiv, { childList: true, subtree: true });

      // Safety net — stop observing after 8 seconds. If Klaviyo hasn't
      // loaded by then, it's not going to. Native form stays visible.
      setTimeout(function() { obs.disconnect(); }, 8000);
    });
  });
})();

/* ============================================================
   dp-line-totals
   ------------------------------------------------------------
   Sticky ATC line-total math + cross-product-type parity.

   Problem: two product paths (theme variant picker + DP options-app
   configurator) both write the running "unit price" to
   `[data-product-price]`. The sticky ATC needs to show
   `unit_price × qty`. Scaling: works for both paths without
   coupling to the options-app's internals.

   Markers (set in Liquid):
     - `[data-product-price]` (no `data-dp-line-total`) = canonical
       unit-price display. Authored by:
         * non-configurator products → updatePrice() in
           dp-product-variants module on variant change
         * configurator products → product-options.js
           updateGlobalPrice() on upsell selection / base swap
     - `[data-dp-line-total]` = line-total display (sticky ATC).
       Wrapped element whose inner .dp-price__current we rewrite.
     - `[data-dp-qty-input]` = quantity input. Standard `name="quantity"`
       also accepted as fallback for backwards compat with non-DP
       sections.

   Triggers:
     1. Direct calls from dp-product-variants onChange (immediate)
     2. Direct call from page init
     3. Native `change` event on the qty input (user types/clicks
        +/-)
     4. MutationObserver on the canonical unit price — catches the
        options-app's updateGlobalPrice writes without us having to
        hook into the app's API surface.

   Money parsing: pulls digits + '.' from the rendered text, then
   parseFloat → cents. Handles "$XX.XX" / "$X,XXX.XX" / "€XX,XX"
   formats roughly; if the merchant uses an exotic decimal
   separator, the math may be off by orders of magnitude. Mitigation
   path if it ever bites: read the cents from a data-* attribute
   the price snippet emits instead of parsing display text.
   ============================================================ */
(function () {
  const DP = window.DP = window.DP || {};

  function parsePriceTextToCents(text) {
    if (!text) return null;
    // Remove everything except digits, periods, and commas.
    // Most stores use "$1,234.56" — strip commas, keep period.
    const cleaned = String(text).replace(/[^0-9.,]/g, '');
    // If there's a comma BEFORE a period, it's a thousands separator: strip.
    // If there's only a comma (European format), treat as decimal point.
    let normalized;
    if (cleaned.indexOf('.') !== -1) {
      normalized = cleaned.replace(/,/g, '');
    } else if (cleaned.indexOf(',') !== -1) {
      normalized = cleaned.replace(/,/g, '.');
    } else {
      normalized = cleaned;
    }
    const num = parseFloat(normalized);
    if (isNaN(num)) return null;
    return Math.round(num * 100);
  }
  // Exposed so dp-price-badges (configurator crossed-out price watcher,
  // earlier in this file) can parse the options-app's live price writes
  // without duplicating this parsing logic.
  DP.parsePriceTextToCents = parsePriceTextToCents;

  function getCanonicalUnitCents() {
    /* First `[data-product-price]` that is NOT a line-total display
       AND NOT inside one. Stops at the first hit since multiple unit
       prices on one PDP would be a bug elsewhere. */
    const candidates = document.querySelectorAll('[data-product-price]');
    for (const el of candidates) {
      if (el.hasAttribute('data-dp-line-total')) continue;
      if (el.closest('[data-dp-line-total]')) continue;
      const current = el.querySelector('.dp-price__current, .price__current') || el;
      const cents = parsePriceTextToCents(current.textContent);
      if (cents != null) return cents;
    }
    return null;
  }

  function getQty() {
    const input = document.querySelector('[data-dp-qty-input], input[name="quantity"]');
    if (!input) return 1;
    const n = parseInt(input.value, 10);
    return n > 0 ? n : 1;
  }

  DP.updateLineTotals = function () {
    const unitCents = getCanonicalUnitCents();
    if (unitCents == null) return;
    const qty = getQty();
    const total = unitCents * qty;
    document.querySelectorAll('[data-dp-line-total]').forEach((wrap) => {
      const current = wrap.querySelector('.dp-price__current, .price__current');
      if (!current) return;
      current.textContent = DP.formatMoney
        ? DP.formatMoney(total)
        : '$' + (total / 100).toFixed(2);
      // Compare-at + savings badges inside the sticky ATC: best-effort
      // mirror the same multiplication on compare-at. Only updates if
      // the elements exist (sticky ATC may or may not render them).
      const compareS = wrap.querySelector('.dp-price__compare s');
      if (compareS) {
        const cmpCents = parsePriceTextToCents(compareS.textContent);
        if (cmpCents != null) {
          compareS.textContent = DP.formatMoney
            ? DP.formatMoney(cmpCents * qty)
            : '$' + ((cmpCents * qty) / 100).toFixed(2);
        }
      }
    });
    /* Sticky ATC qty indicator: show "× N" when qty > 1, hide at 1.
       Keeps the sticky ATC compact at the most common case while
       surfacing multi-buy state when relevant. */
    document.querySelectorAll('[data-dp-line-qty]').forEach((el) => {
      if (qty > 1) {
        el.textContent = '× ' + qty;
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    });
    /* Regular ATC button label: "Add to cart • $X.XX" — applies to
       BOTH non-configurator products (where we are the only writer)
       AND configurator products (where the options-app also writes
       on variant/upsell change). For configurator products, the
       options-app updates the canonical `[data-product-price]`
       element which our MutationObserver picks up and feeds into
       `total` here — so our value matches what the options-app
       computes. The two writers can race on a variant/upsell event
       but converge to the same final string; on a qty event the
       options-app doesn't fire, so OUR write is the only one that
       reflects the multi-buy total.

       Merchant feedback (2026-05-13): the regular ATC button on
       cherry-icebergo-pbt-keyset and similar non-configurator
       products doesn't show the price (61290ca added that path).
       Merchant feedback (2026-05-15, QA D-001): the ATC button on
       Iris SE (configurator) doesn't reflect qty increases — that
       was caused by the original skip-if-configurator condition,
       since the options-app doesn't watch qty changes. Removed. */
    {
      const formatted = DP.formatMoney
        ? DP.formatMoney(total)
        : '$' + (total / 100).toFixed(2);
      document.querySelectorAll('.btn--add-to-cart .btn__text, .dp-product__atc .btn__text').forEach((textEl) => {
        /* Skip "Sold out" / "Unavailable" — those are set by
           updateAvailability and shouldn't be overwritten with a price. */
        if (/sold out|unavailable/i.test(textEl.textContent)) return;
        /* Cache the original label (without any price suffix) on first
           encounter. Subsequent rewrites preserve whatever the merchant
           configured (e.g. "Buy now" instead of "Add to cart"). */
        if (!textEl.dataset.dpAtcLabel) {
          const m = textEl.textContent.match(/^(.+?)\s*(?:[•·\-–—]\s*)?\$[\d.,]+.*$/);
          textEl.dataset.dpAtcLabel = (m ? m[1] : textEl.textContent).trim();
        }
        const desired = textEl.dataset.dpAtcLabel + ' • ' + formatted;
        if (textEl.textContent !== desired) textEl.textContent = desired;
      });
    }
  };

  /* PDP qty stepper +/− handlers. Scoped to `[data-dp-pdp-qty-action]`
     to avoid the cart-drawer's `qty-increment` / `qty-decrement` action
     names (those call /cart/change on the server). PDP qty is purely
     local — modifies the form's hidden quantity field that submits
     when the user clicks ATC. */
  document.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest && e.target.closest('[data-dp-pdp-qty-action]');
    if (!btn) return;
    const wrap = btn.closest('[data-dp-pdp-qty]');
    if (!wrap) return;
    const input = wrap.querySelector('[data-dp-qty-input]');
    if (!input) return;
    const action = btn.getAttribute('data-dp-pdp-qty-action');
    const min = parseInt(input.getAttribute('min'), 10) || 1;
    const current = parseInt(input.value, 10);
    const safe = isNaN(current) ? min : current;
    const next = action === 'increment' ? safe + 1 : Math.max(min, safe - 1);
    if (next === safe) return;
    input.value = next;
    /* Dispatch both `input` and `change` so updateLineTotals fires
       (it listens for both) and any other module subscribed via
       native events catches the update. */
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  function init() {
    /* Cache original ATC button labels on init so subsequent writes
       can prepend them correctly. Also mirror to `dpOriginalLabel`
       (which updateAvailability already reads at line ~679 to
       restore from "Sold out" → original label) so we have a single
       source of truth across both modules. */
    document.querySelectorAll('.btn--add-to-cart .btn__text, .dp-product__atc .btn__text').forEach((textEl) => {
      if (textEl.dataset.dpAtcLabel) return;
      const original = textEl.textContent.trim();
      /* Strip any pre-existing price suffix in case the server already
         rendered the price (rare — usually only happens on configurator
         products where the options-app would manage anyway). */
      const m = original.match(/^(.+?)\s*(?:[•·\-–—]\s*)?\$[\d.,]+.*$/);
      const label = (m ? m[1] : original).trim();
      textEl.dataset.dpAtcLabel = label;
      const atcBtn = textEl.closest('.dp-product__atc, .btn--add-to-cart');
      if (atcBtn && !atcBtn.dataset.dpOriginalLabel) {
        atcBtn.dataset.dpOriginalLabel = label;
      }
    });
    /* Native `change` on the qty input. The qty stepper is a plain
       number input; +/− on dp-cart-items uses a different selector
       (data-dp-action) so this won't conflict. */
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!t) return;
      if (t.matches('[data-dp-qty-input], input[name="quantity"]')) {
        DP.updateLineTotals();
      }
    });
    /* Also catch `input` events for instant feedback as the user
       types the number (change fires only on blur in some browsers). */
    document.addEventListener('input', (e) => {
      const t = e.target;
      if (!t) return;
      if (t.matches('[data-dp-qty-input], input[name="quantity"]')) {
        DP.updateLineTotals();
      }
    });
    /* MutationObserver on the canonical unit price. Catches the
       options-app's updateGlobalPrice writes (which we have no hook
       into) so sticky ATC stays in sync without coupling to the app.
       Observe attribute + childList changes on the wrap; the
       .dp-price__current text content is what we read. */
    const candidates = document.querySelectorAll('[data-product-price]');
    candidates.forEach((el) => {
      if (el.hasAttribute('data-dp-line-total')) return;
      if (el.closest('[data-dp-line-total]')) return;
      const observer = new MutationObserver(() => DP.updateLineTotals());
      observer.observe(el, { childList: true, characterData: true, subtree: true });
    });
    /* Init pass. */
    DP.updateLineTotals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
